import { NamespacedIconName } from 'src/app/shared/constants/material-icon-name';
import {
  MASTER_DATA_SECTIONS,
  MASTER_DATA_TAXONOMY,
} from '@modules/master-data/master-data-taxonomy';

/**
 * Sub-navigation configuration for each module in the context sidebar.
 *
 * Keys are the first path segment (e.g. '/sales').
 * Values define the ordered list of sub-pages with i18n labels and Material icons.
 */
export interface SubNavItem {
  label: string;
  path: string;
  icon?: NamespacedIconName;
  badge?: number;
  section?: string;
  /** Optional total count chip rendered next to the section header. Only honored when `section` is set. */
  sectionCount?: number;
  /** Optional count chip rendered to the right of the item label. */
  count?: number;
  /**
   * Item lifecycle status. `planned` items render dimmed (opacity-60) with a PLAN badge — they're
   * surfaced in nav so reviewers can see what's coming, but their routes are stubs.
   */
  status?: 'planned';
  /**
   * True when the item's route is an InDevelopment stub (screen not built yet). Nav renders
   * the item normally; the shared FeatureMapComponent uses this to badge "Đang xây dựng".
   */
  inDev?: boolean;
  /** RBAC resource code — when set, the item is hidden if the user lacks this permission. */
  resourceId?: string;
  /**
   * Chỉ sáng khi URL TRÙNG ĐÚNG `path`, không sáng cho trang con. Dành cho mục "trang chủ" của
   * một khu vực mà mọi trang khác nằm dưới nó nhưng KHÔNG phải trang con của nó — Trang chủ thư
   * viện sáng cả khi mở `components/v2-kit` vì so khớp tiền tố dài nhất rơi về nó. Đừng đặt cho
   * mục là danh sách có trang chi tiết (vd `/knowledge`): trang chi tiết phải sáng mục cha.
   */
  exact?: boolean;
}

export const MODULE_TITLE_MAP: Record<string, string> = {
  '/sales': 'Navigation.SalesManagement',
  '/warehouse': 'Navigation.WarehouseManagement',
  '/crm': 'Navigation.CRM',
  '/procurement': 'Navigation.Procurement',
  '/finance': 'Navigation.FinanceManagement',
  '/manufacturing': 'Navigation.ManufacturingManagement',
  '/logistics': 'Navigation.LogisticManagement',
  '/hrm': 'Navigation.HRM',
  '/planning': 'Navigation.Planning',
  '/master-data': 'Navigation.MasterData.Title',
  '/administration': 'Navigation.Administration',
  '/security': 'Navigation.Security',
  '/platform': 'Navigation.Platform',
  '/governance': 'Navigation.Governance',
  '/pos': 'Navigation.StoreManagement',
  '/project-management': 'Navigation.ProjectManagement',
  '/asset-management': 'Navigation.AssetManagement',
  '/fusion': 'Navigation.Fusion',
  '/okr-kpi': 'Navigation.OkrKpi',
  '/risk': 'Navigation.RiskManagement',
  '/profile': 'Navigation.Profile',
  '/notification': 'Navigation.Notifications',
  '/knowledge': 'Navigation.KnowledgeHub',
  '/pricing': 'Navigation.Pricing',
  '/component-library': 'Navigation.ComponentLibrary',
  '/test-center': 'Navigation.TestCenter',
};

const masterDataSubNav: SubNavItem[] = MASTER_DATA_SECTIONS.flatMap((section) =>
  MASTER_DATA_TAXONOMY.filter((e) => e.section === section.key).map(
    (entity, idx): SubNavItem => ({
      label: entity.navLabelKey,
      path: `/master-data/${entity.route}`,
      icon: entity.iconOutline,
      ...(idx === 0 ? { section: section.navTitleKey } : {}),
    }),
  ),
);

export const moduleSubNavConfig: Record<string, SubNavItem[]> = {
  '/test-center': [
    // ── ĐIỀU HÀNH ──
    {
      label: 'TestCenter.Dashboard.Title',
      path: '/test-center/dashboard',
      icon: 'mat_outline:dashboard',
      section: 'Navigation.Sections.TestCenterOperations',
    },
    {
      label: 'TestCenter.Analytics.Title',
      path: '/test-center/analytics',
      icon: 'mat_outline:insights',
    },
    // ── LẬP KẾ HOẠCH ──
    {
      label: 'TestCenter.Suites.Title',
      path: '/test-center/suites',
      icon: 'mat_outline:folder_special',
      section: 'Navigation.Sections.TestCenterPlanning',
    },
    {
      label: 'TestCenter.Cases.Title',
      path: '/test-center/cases',
      icon: 'mat_outline:list_alt',
    },
    {
      label: 'TestCenter.Cycles.Title',
      path: '/test-center/cycles',
      icon: 'mat_outline:fact_check',
    },
    {
      label: 'TestCenter.Scheduler.Title',
      path: '/test-center/scheduler',
      icon: 'mat_outline:schedule',
    },
    {
      label: 'TestCenter.Traceability.Title',
      path: '/test-center/traceability',
      icon: 'mat_outline:account_tree',
    },
    // ── THỰC THI ──
    {
      label: 'TestCenter.BusinessFlow.Title',
      path: '/test-center/business-flow',
      icon: 'mat_outline:account_tree',
      section: 'Navigation.Sections.TestCenterExecution',
    },
    {
      label: 'TestCenter.Integration.Title',
      path: '/test-center/integration',
      icon: 'mat_outline:sync_alt',
    },
    {
      label: 'TestCenter.Feature.Title',
      path: '/test-center/feature',
      icon: 'mat_outline:widgets',
    },
    {
      label: 'TestCenter.Api.Title',
      path: '/test-center/api',
      icon: 'mat_outline:api',
    },
    {
      label: 'TestCenter.Frontend.Title',
      path: '/test-center/frontend',
      icon: 'mat_outline:devices',
    },
    {
      label: 'TestCenter.Impact.Title',
      path: '/test-center/impact',
      icon: 'mat_outline:change_history',
    },
    {
      label: 'TestCenter.Runs.Title',
      path: '/test-center/runs',
      icon: 'mat_outline:history',
    },
    // ── CHẤT LƯỢNG ──
    {
      label: 'TestCenter.Defects.Title',
      path: '/test-center/defects',
      icon: 'mat_outline:bug_report',
      section: 'Navigation.Sections.TestCenterQuality',
    },
    {
      label: 'TestCenter.Coverage.Title',
      path: '/test-center/coverage',
      icon: 'mat_outline:shield',
    },
    {
      label: 'TestCenter.Compliance.Title',
      path: '/test-center/compliance',
      icon: 'mat_outline:gavel',
    },
    {
      label: 'TestCenter.Release.Title',
      path: '/test-center/release',
      icon: 'mat_outline:verified',
    },
    // ── NỀN TẢNG ──
    {
      label: 'TestCenter.TestData.Title',
      path: '/test-center/test-data',
      icon: 'mat_outline:dataset',
      section: 'Navigation.Sections.TestCenterPlatform',
    },
    {
      label: 'TestCenter.Landscape.Title',
      path: '/test-center/landscape',
      icon: 'mat_outline:lan',
    },
    {
      label: 'TestCenter.Roles.Title',
      path: '/test-center/roles',
      icon: 'mat_outline:groups',
    },
    {
      label: 'TestCenter.Webhooks.Title',
      path: '/test-center/webhooks',
      icon: 'mat_outline:webhook',
    },
    {
      label: 'TestCenter.WebhookLog.Title',
      path: '/test-center/webhook-log',
      icon: 'mat_outline:receipt_long',
    },
    {
      label: 'TestCenter.Settings.Title',
      path: '/test-center/settings',
      icon: 'mat_outline:settings',
      section: 'Navigation.Sections.TestCenterConfig',
    },
  ],
  '/pricing': [
    {
      label: 'Navigation.PriceSimulation',
      path: '/pricing/simulation',
      icon: 'mat_outline:calculate',
      section: 'Navigation.Sections.PriceEngine',
    },
    {
      label: 'Navigation.PriceCalculator',
      path: '/pricing/calculator',
      icon: 'mat_outline:calculate',
    },
    {
      label: 'Navigation.PriceAnalytics',
      path: '/pricing/analytics',
      icon: 'mat_outline:analytics',
    },
    {
      label: 'Navigation.PriceLists',
      path: '/pricing/price-list',
      icon: 'mat_outline:sell',
    },
    {
      label: 'Navigation.CustomerPricing',
      path: '/pricing/customer-pricing',
      icon: 'mat_outline:person',
    },
    {
      label: 'Navigation.ContractPricing',
      path: '/pricing/contract-pricing',
      icon: 'mat_outline:description',
    },
    {
      label: 'Navigation.VolumeDiscounts',
      path: '/pricing/volume-discounts',
      icon: 'mat_outline:stacked_bar_chart',
    },
    {
      label: 'Navigation.ExclusionGroups',
      path: '/pricing/exclusion-groups',
      icon: 'mat_outline:block',
    },
    {
      label: 'Navigation.PriceConditionType',
      path: '/pricing/condition-type',
      icon: 'mat_outline:grid_view',
      section: 'Navigation.Sections.ConditionTechnique',
    },
    {
      label: 'Navigation.PriceConditionTable',
      path: '/pricing/condition-table',
      icon: 'mat_outline:table_chart',
    },
    {
      label: 'Navigation.PriceAccessSequence',
      path: '/pricing/access-sequence',
      icon: 'mat_outline:format_list_numbered',
    },
    {
      label: 'Navigation.PricingProcedure',
      path: '/pricing/pricing-procedure',
      icon: 'mat_outline:account_tree',
    },
    {
      label: 'Navigation.PriceConditionRecord',
      path: '/pricing/condition-record',
      icon: 'mat_outline:sell',
    },
    {
      label: 'Navigation.PriceTrace',
      path: '/pricing/trace',
      icon: 'mat_outline:troubleshoot',
    },
    {
      label: 'Navigation.TimeWindows',
      path: '/pricing/time-windows',
      icon: 'mat_outline:schedule',
      section: 'Navigation.Sections.TimePricing',
    },
    {
      label: 'Navigation.ItemTimePricing',
      path: '/pricing/item-time-pricing',
      icon: 'mat_outline:access_time',
    },
    {
      label: 'Navigation.PriceApproval',
      path: '/pricing/price-approval',
      icon: 'mat_outline:approval',
      section: 'Navigation.Sections.PricingOperations',
    },
    {
      label: 'Navigation.PriceChangeLog',
      path: '/pricing/change-log',
      icon: 'mat_outline:history',
    },
    {
      label: 'Navigation.PricingGuide',
      path: '/pricing/guide',
      icon: 'mat_outline:help_outline',
      section: 'Navigation.Sections.PricingHelp',
    },
  ],
  '/sales': [
    // Reconciled to the Carot "Quản lý bán hàng" design IA (sales-shell.jsx · NAV) —
    // 6 groups · 19 features in design order. Section headers sit on the first item of
    // each group. Items whose screen/API does not exist yet route to the shared
    // InDevelopmentComponent ("Đang phát triển") — see modules/sales/sales.routes.ts.
    // ── ĐIỀU HÀNH ──
    {
      label: 'Navigation.ControlTower',
      path: '/sales/control-tower',
      icon: 'dashboard',
      section: 'Navigation.Sections.SalesOperations',
      resourceId: 'SM-SAL-CT',
    },
    {
      label: 'Navigation.SalesAnalytics',
      path: '/sales/analytics',
      icon: 'pie_chart',
      inDev: true,
      resourceId: 'SM-SAL-ANL',
    },
    {
      label: 'Navigation.SalesTargets',
      path: '/sales/sales-targets',
      icon: 'track_changes',
      inDev: true,
      resourceId: 'SM-SAL-TGT',
    },
    // ── BÁN HÀNG ──
    {
      label: 'Navigation.SalesChannels',
      path: '/sales/channels',
      icon: 'hub',
      section: 'Navigation.Sections.SalesSelling',
      inDev: true,
      resourceId: 'SM-SAL-CHN',
    },
    {
      label: 'Navigation.SalesOrders',
      path: '/sales/order',
      icon: 'shopping_cart',
      resourceId: 'SM-SAL-SOV2',
    },
    {
      // Đứng ngay sau Đơn bán: phiếu luôn sinh ra từ một đơn, và kế toán bán hàng mở mục này
      // hằng ngày để xử lý hàng chờ xác nhận.
      // `resourceId` chép từ `resourceUniqueCode` của CHÍNH route (`sales.routes.ts`) — thiếu nó
      // thì mục hiện với mọi tài khoản, kể cả tài khoản không mở được màn.
      label: 'ActualRevision.List.Title',
      path: '/sales/actual-revision',
      icon: 'scale',
      resourceId: 'SM-SAL-SOV2',
    },
    {
      label: 'Navigation.Invoices',
      path: '/sales/invoice',
      icon: 'receipt_long',
      resourceId: 'SM-SAL-INV',
    },
    {
      label: 'Navigation.Quotations',
      path: '/sales/quotation',
      icon: 'request_quote',
      resourceId: 'SM-SAL-QT',
    },
    {
      // US-SAL-11 — luật duyệt bán hàng theo BU; cùng resource với màn báo giá (cùng route cha).
      label: 'Navigation.SalesApprovalRules',
      path: '/sales/quotation/approval-rules',
      icon: 'rule',
      resourceId: 'SM-SAL-QT',
    },
    {
      label: 'Navigation.SalesContracts',
      path: '/sales/sales-contract',
      icon: 'contract',
      resourceId: 'SM-SAL-SC',
    },
    {
      label: 'Navigation.SalesReturns',
      path: '/sales/returns',
      icon: 'assignment_return',
      inDev: true,
      resourceId: 'SM-SAL-RET',
    },
    // ── THU TIỀN & GIAO HÀNG ──
    {
      // DEC-025 (05/09): màn "Thanh toán khách hàng" đã xoá — thu tiền đi qua phiếu thu hợp nhất.
      label: 'Navigation.Receipt',
      path: '/sales/accounting/receipt',
      icon: 'payments',
      section: 'Navigation.Sections.SalesCollection',
      resourceId: 'SM-SAC-RN',
    },
    // Phiếu chi và Tài khoản khách hàng nằm THẲNG ở đây, không núp sau trang ô vuông.
    //
    // Cùng lớp với việc đã làm cho `/sales/delivery` hôm 10/09: `/sales/accounting` là một lưới
    // ô vuông chỉ để bấm tiếp, và đo được **0 nơi trong repo link tới chính nó** — nên nó không
    // phải lối vào, nó là một trang không ai tới được. Sáu màn dưới nó (payment, customer-account,
    // customer-debt, credit-limit, dunning, ar-confirmation) do đó **không có lối vào nào**, dù
    // mỗi màn 445–663 dòng và đã chạy được. Module logistics đã đưa cùng bộ màn ấy lên sidebar
    // của nó từ trước; đây là chép lại mẫu đã chạy, không phải thiết kế mới.
    //
    // `resourceId` chép từ `resourceUniqueCode` của CHÍNH route (`sales/accounting/accounting.routes.ts`),
    // không chép từ trang ô vuông cũ. Gõ sai mã thì mục biến mất với mọi tài khoản không phải
    // admin, không log, không lỗi — và admin không thấy vì nhánh isAdmin bỏ qua kiểm tra.
    {
      label: 'Payment.Title',
      path: '/sales/accounting/payment',
      icon: 'payments',
      resourceId: 'SM-SAC-PM',
    },
    {
      label: 'CustomerAccount.Title',
      path: '/sales/accounting/customer-account',
      icon: 'account_balance_wallet',
      resourceId: 'SM-SAC-AB',
    },
    // Năm màn giao hàng nằm THẲNG ở đây, không núp sau một trang ô vuông.
    //
    // Trước 10/09 sidebar chỉ có một mục "Giao hàng" trỏ `/sales/delivery`, và màn ấy là một
    // lưới ô vuông chỉ để bấm tiếp — điều hướng chồng lên điều hướng, trái ADR-045 (sidebar là
    // đường điều hướng chính). Đo được: **0/5** đích của nó có mặt trong sidebar, nên trang ô
    // vuông là lối vào DUY NHẤT tới cả năm màn. Vì thế phải thêm vào đây TRƯỚC khi xoá nó.
    //
    // `resourceId` chép từ `resourceUniqueCode` của chính route (nguồn sự thật), không chép từ
    // trang ô vuông cũ.
    {
      label: 'DeliveryNote.Title',
      path: '/sales/delivery/delivery-note',
      icon: 'local_shipping',
      resourceId: 'SM-DLV-DN',
    },
    {
      label: 'DeliverySlip.Title',
      path: '/sales/delivery/delivery-slip',
      icon: 'receipt',
      resourceId: 'SM-DLV-DS',
    },
    {
      label: 'Shipment.Title',
      path: '/sales/delivery/shipment',
      icon: 'local_shipping',
      resourceId: 'SM-DLV-SM',
    },
    {
      label: 'DeliveryPoint.Title',
      path: '/sales/delivery/delivery-point',
      icon: 'pin_drop',
      resourceId: 'SM-DLV-DN',
    },
    {
      label: 'FreightRate.Title',
      path: '/sales/delivery/freight-rate',
      icon: 'price_change',
      resourceId: 'SM-DLV-SM',
    },
    {
      label: 'Navigation.ARAgingReport',
      path: '/sales/ar-aging',
      icon: 'hourglass_bottom',
      resourceId: 'SM-SAL-AR',
    },
    // ── CÔNG NỢ KHÁCH HÀNG ──
    // Nhóm này có trong design IA từ đầu và vắng hẳn khỏi sidebar cho tới 10/09. Bốn màn đã dựng
    // xong 08/09 (572 · 445 · 449 · 597 dòng) mà không màn nào có lối vào, trong khi tháp điều
    // hành ngay trên nó hiển thị "công nợ phải thu · quá hạn" — hứa một con số rồi khoá cửa
    // đúng bốn màn dùng để thu số đó.
    {
      label: 'CustomerDebt.Title',
      path: '/sales/accounting/customer-debt',
      icon: 'account_balance',
      section: 'Navigation.Sections.SalesDebt',
      resourceId: 'SM-SAC-CR',
    },
    {
      label: 'CreditLimit.Title',
      path: '/sales/accounting/customer-debt/credit-limit',
      icon: 'speed',
      resourceId: 'SM-SAC-CR',
    },
    {
      label: 'Dunning.Title',
      path: '/sales/accounting/customer-debt/dunning',
      icon: 'notifications_active',
      resourceId: 'SM-SAC-CR',
    },
    {
      label: 'ArConfirm.Title',
      path: '/sales/accounting/customer-debt/ar-confirmation',
      icon: 'fact_check',
      resourceId: 'SM-SAC-CR',
    },
    // ── HOA HỒNG ──
    {
      label: 'Navigation.CommissionPlans',
      path: '/sales/commission-plans',
      icon: 'assignment',
      section: 'Navigation.Sections.SalesCommission',
      resourceId: 'SM-SAL-CMS',
    },
    {
      label: 'Navigation.CommissionStatement',
      path: '/sales/commission-statement',
      icon: 'summarize',
      resourceId: 'SM-SAL-CST',
    },
    // ── KHUYẾN MÃI ──
    {
      label: 'Navigation.CreditNotes',
      path: '/sales/credit-note',
      icon: 'sell',
      section: 'Navigation.Sections.SalesPromotion',
      resourceId: 'SM-SAL-CN',
    },
    {
      label: 'Navigation.B2BPromotions',
      path: '/sales/promotions',
      icon: 'campaign',
      resourceId: 'SM-SAL-PRM',
    },
    {
      label: 'Navigation.PromotionBudget',
      path: '/sales/promotion-budget',
      icon: 'savings',
      resourceId: 'SM-SAL-PBG',
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.SalesFeatureMap',
      path: '/sales/feature-map',
      icon: 'layers',
      section: 'Navigation.Sections.SalesHelp',
      resourceId: 'SM-HELP',
    },
    {
      label: 'Navigation.SalesGuide',
      path: '/sales/guide',
      icon: 'help',
      inDev: true,
      resourceId: 'SM-HELP',
    },
  ],
  '/warehouse': [
    // Reconciled to the Carot Warehouse design IA (wh-shell.jsx · NAV_GROUPS) —
    // 10 design groups + "Danh mục kho" (Q3, Alex 2026-08-11). Items with inDev:true
    // route to the shared InDevelopmentComponent until their wave of epic
    // WH-HIFI-01 lands (W1 approval-requests · W2 transaction-history · W3 accounting ×4 · W5 help ×2).
    // ── TỔNG QUAN ──
    // E-026: "Tổng quan kho" gỡ khỏi thanh bên — Tháp điều khiển kho thay nó.
    {
      label: 'Navigation.WarehouseControlTower',
      path: '/warehouse/control-tower',
      icon: 'mat_outline:speed',
      section: 'Navigation.Sections.Overview',
      resourceId: 'WH-DASH',
    },
    {
      label: 'Navigation.InventoryDashboard',
      path: '/warehouse/inventory-dashboard',
      icon: 'mat_outline:assessment',
      resourceId: 'WH-DASH',
    },
    {
      // Bảng theo dõi việc đang dở của tổ kho (7 chặng: nhập → QC → cất → lưu trữ → lấy →
      // đóng gói → xuất giao). Trước đây nằm ở nhóm TỒN KHO dưới tên "Dòng chảy tồn kho",
      // trùng tên với BÁO CÁO cuộn số dư ở nhóm Báo cáo — hai màn trả lời hai câu khác hẳn
      // nhau: cái này là "đang tắc ở khâu nào", cái kia là "kỳ này tồn biến động ra sao".
      label: 'Navigation.WarehouseFlowBoard',
      path: '/warehouse/inventory-flow',
      icon: 'mat_outline:sync_alt',
      resourceId: 'WH-FLOW',
    },
    // ── NHẬP KHO ──
    {
      label: 'Navigation.InboundOutbound',
      path: '/warehouse/inbound-outbound',
      icon: 'mat_outline:swap_horiz',
      section: 'Navigation.Sections.Inbound',
      resourceId: 'WH-INB',
    },
    {
      label: 'Navigation.GoodsReceipt',
      path: '/warehouse/goods-receipt',
      icon: 'mat_outline:move_to_inbox',
      resourceId: 'WM-GR',
    },
    {
      label: 'Navigation.WarehouseQualityInspection',
      path: '/warehouse/quality-inspection',
      icon: 'mat_outline:fact_check',
      resourceId: 'WM-INB-IS',
    },
    {
      label: 'Navigation.PutAwayOrder',
      path: '/warehouse/put-away-order',
      icon: 'mat_outline:archive',
      resourceId: 'WM-INB-PA',
    },
    {
      // US-WH-31 — quy tắc cất hàng; cùng resource với màn cất hàng (cùng route cha).
      label: 'Navigation.PutAwayRules',
      path: '/warehouse/put-away-order/rules',
      icon: 'rule',
      resourceId: 'WM-INB-PA',
    },
    {
      // US-WH-43: route `handling-unit` có từ lâu nhưng không mục nào trỏ tới — người dùng chỉ
      // tới được bằng gõ tay URL. Đặt ở nhóm NHẬP KHO cho khớp breadcrumb của route
      // (SEC_INBOUND) và mã quyền `WM-INB-HU` mà route đã khai.
      label: 'Navigation.HandlingUnit',
      path: '/warehouse/handling-unit',
      icon: 'pallet',
      resourceId: 'WM-INB-HU',
    },
    {
      label: 'Navigation.ReturnNote',
      path: '/warehouse/return-note',
      icon: 'mat_outline:assignment_return',
      resourceId: 'WM-RN',
    },
    // ── XUẤT KHO ──
    {
      label: 'Navigation.GoodsIssue',
      path: '/warehouse/goods-issue',
      icon: 'mat_outline:outbox',
      section: 'Navigation.Sections.Outbound',
      resourceId: 'WM-GI',
    },
    {
      label: 'Navigation.Picking',
      path: '/warehouse/picking',
      icon: 'mat_outline:checklist',
      resourceId: 'WM-PCK',
    },
    {
      label: 'Navigation.PackingList',
      path: '/warehouse/packing-list',
      icon: 'mat_outline:inventory_2',
      resourceId: 'WM-PKL',
    },
    {
      label: 'Navigation.Shipment',
      path: '/warehouse/shipment',
      icon: 'mat_outline:local_shipping',
      resourceId: 'WM-SHP',
    },
    {
      label: 'Navigation.WavePlanning',
      path: '/warehouse/wave-planning',
      icon: 'mat_outline:waves',
      resourceId: 'WM-WP',
    },
    // ── PHÊ DUYỆT (GR-WF-02 E9 · WH-HIFI-01 W1) ──
    {
      label: 'Navigation.WarehouseApprovalRequests',
      path: '/warehouse/approval-requests',
      icon: 'mat_outline:task_alt',
      section: 'Navigation.Sections.WarehouseApproval',
      resourceId: 'WM-APR',
    },
    {
      label: 'Navigation.WarehouseApprovalQueue',
      path: '/warehouse/approval',
      icon: 'mat_outline:schedule',
      resourceId: 'WM-APR-WF',
    },
    // ── TỒN KHO ──
    {
      label: 'Navigation.InventoryStatus',
      path: '/warehouse/inventory-status',
      icon: 'mat_outline:inventory',
      section: 'Navigation.Sections.Inventory',
      resourceId: 'WM-IS',
    },
    {
      label: 'Navigation.InventoryBalance',
      path: '/warehouse/inventory-balance',
      icon: 'mat_outline:inventory',
      resourceId: 'WM-IB',
    },
    {
      label: 'Navigation.StockTransfer',
      path: '/warehouse/stock-transfer',
      icon: 'mat_outline:swap_calls',
      resourceId: 'WM-ST',
    },
    {
      label: 'Navigation.InventoryAdjustment',
      path: '/warehouse/inventory-adjustment',
      icon: 'mat_outline:tune',
      resourceId: 'WM-IA',
    },
    {
      label: 'Navigation.CycleCount',
      path: '/warehouse/cycle-count',
      icon: 'mat_outline:fact_check',
      resourceId: 'WM-CC',
    },
    {
      label: 'Navigation.WarehouseTransactionHistory',
      path: '/warehouse/transaction-history',
      icon: 'mat_outline:history',
      resourceId: 'WM-TRH',
    },
    {
      // US-WH-36 — Thẻ kho: cùng sổ + cùng quyền với lịch sử giao dịch kho.
      label: 'Navigation.WarehouseStockCard',
      path: '/warehouse/stock-card',
      icon: 'mat_outline:receipt_long',
      resourceId: 'WM-TRH',
    },
    {
      label: 'Navigation.WarehouseDocuments',
      path: '/warehouse/documents',
      icon: 'mat_outline:description',
      resourceId: 'WM-DOC',
    },
    // ── BÁO CÁO & PHÂN TÍCH ──
    {
      // Báo cáo cuộn số dư theo kỳ: đầu kỳ → nhập → xuất → điều chỉnh → cuối kỳ, đối chiếu
      // từng mặt hàng với InventoryBalance live. Giữ tên "Dòng chảy tồn kho" theo design.
      label: 'Navigation.InventoryFlow',
      path: '/warehouse/reports/inventory-rollforward',
      icon: 'mat_outline:trending_up',
      section: 'Navigation.Sections.WarehouseReports',
      resourceId: 'WH-RPT',
    },
    {
      label: 'Navigation.WasteAnalytics',
      path: '/warehouse/waste-analytics',
      icon: 'mat_outline:warning_amber',
      resourceId: 'WH-WASTE',
    },
    {
      label: 'Navigation.InventoryAging',
      path: '/warehouse/reports/inventory-aging',
      icon: 'mat_outline:access_time',
      resourceId: 'WH-RPT',
    },
    {
      label: 'Navigation.DeadStock',
      path: '/warehouse/reports/dead-stock',
      icon: 'mat_outline:inventory_2',
      resourceId: 'WH-RPT',
    },
    {
      label: 'Navigation.LaborProductivity',
      path: '/warehouse/reports/labor-productivity',
      icon: 'mat_outline:groups',
      resourceId: 'WH-RPT',
    },
    // ── THEO DÕI & TÁC VỤ ──
    {
      label: 'Navigation.LotTraceability',
      path: '/warehouse/lot-traceability',
      icon: 'mat_outline:track_changes',
      section: 'Navigation.Sections.Tracking',
      resourceId: 'WM-TRC',
    },
    {
      // US-WH-56 — báo cáo thu hồi lô (cùng tài nguyên/quyền với màn truy xuất).
      label: 'Navigation.LotRecallReport',
      path: '/warehouse/lot-recall-report',
      icon: 'mat_outline:assignment_return',
      resourceId: 'WM-TRC',
    },
    {
      label: 'Navigation.TaskManagement',
      path: '/warehouse/task-management',
      icon: 'mat_outline:assignment',
      resourceId: 'WM-TM',
    },
    // ── KHO ẢO ──
    {
      label: 'Navigation.VirtualWarehouse',
      path: '/warehouse/virtual-warehouse',
      icon: 'mat_outline:cloud',
      section: 'Navigation.Sections.Virtual',
      resourceId: 'WM-VW',
    },
    {
      label: 'Navigation.VirtualReconciliation',
      path: '/warehouse/virtual-reconciliation',
      icon: 'mat_outline:compare_arrows',
      resourceId: 'WM-VR',
    },
    // ── KẾ TOÁN KHO (WH-REDESIGN-01 + WH-HIFI-01 W3) ──
    {
      label: 'Navigation.WarehouseGLPosting',
      path: '/warehouse/gl-posting',
      icon: 'mat_outline:account_balance',
      section: 'Navigation.Sections.WarehouseAccounting',
      resourceId: 'WM-GLP',
    },
    {
      label: 'Navigation.WarehouseGLConfig',
      path: '/warehouse/gl-config',
      icon: 'mat_outline:rule',
      resourceId: 'WM-GLC',
    },
    {
      label: 'Navigation.MaterialLedger',
      path: '/warehouse/material-ledger',
      icon: 'mat_outline:account_balance_wallet',
      resourceId: 'WM-ML',
    },
    {
      label: 'Navigation.InventoryRevaluation',
      path: '/warehouse/revaluation',
      icon: 'mat_outline:trending_up',
      resourceId: 'WM-RVL',
    },
    {
      label: 'Navigation.WarehousePriceVariance',
      path: '/warehouse/price-variance',
      icon: 'mat_outline:attach_money',
      resourceId: 'WM-PV',
    },
    {
      // US-WH-37 — Độ trôi sổ kho: cùng sổ + cùng quyền với lịch sử giao dịch kho / thẻ kho.
      label: 'Navigation.InventoryDrift',
      path: '/warehouse/inventory-drift',
      icon: 'mat_outline:compare_arrows',
      resourceId: 'WM-TRH',
    },
    {
      label: 'Navigation.WarehousePeriodClose',
      path: '/warehouse/period-close',
      icon: 'mat_outline:event_available',
      resourceId: 'WM-PCL',
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.WarehouseFeatureMap',
      path: '/warehouse/feature-map',
      icon: 'mat_outline:map',
      section: 'Navigation.Sections.Help',
      resourceId: 'WM-HELP',
    },
    {
      label: 'Navigation.WarehouseGuide',
      path: '/warehouse/guide',
      icon: 'mat_outline:menu_book',
      resourceId: 'WM-HELP',
    },
    // ── DANH MỤC KHO (Q3 · Alex 2026-08-11) ──
    {
      label: 'Navigation.Batch',
      path: '/warehouse/batch',
      icon: 'mat_outline:layers',
      section: 'Navigation.Sections.WarehouseMasterData',
      resourceId: 'WM-INB-BA',
    },
    {
      label: 'Navigation.LotNumber',
      path: '/warehouse/lot-number',
      icon: 'mat_outline:tag',
      resourceId: 'WM-INB-LO',
    },
    {
      label: 'Navigation.SerialNumber',
      path: '/warehouse/serial',
      icon: 'mat_outline:format_list_numbered',
      resourceId: 'WM-INB-SE',
    },
    {
      label: 'Navigation.Warranty',
      path: '/warehouse/warranty',
      icon: 'mat_outline:verified',
      resourceId: 'WM-INB-WR',
    },
    {
      label: 'Navigation.Package',
      path: '/warehouse/package',
      icon: 'mat_outline:inventory_2',
      resourceId: 'WM-INB-PKG',
    },
  ],
  '/crm': [
    // Reconciled to the Carot CRM "Khách hàng" design IA (crm-core.jsx · NAV) —
    // 8 groups · 25 features in design order. Section headers sit on the first item of
    // each group. Items whose screen/API does not exist yet route to the shared
    // InDevelopmentComponent ("Đang phát triển") — see modules/crm/crm.routes.ts.
    // ── ĐIỀU HÀNH ──
    {
      label: 'Navigation.CrmOverview',
      path: '/crm/dashboard',
      icon: 'mat_outline:dashboard',
      section: 'Navigation.Sections.CrmOperations',
    },
    {
      label: 'Navigation.CrmActivity',
      path: '/crm/activity',
      icon: 'mat_outline:bolt',
    },
    // ── KHÁCH HÀNG ──
    {
      label: 'Navigation.CrmContacts',
      path: '/crm/customer',
      icon: 'mat_outline:contacts',
      section: 'Navigation.Sections.CrmCustomers',
    },
    {
      label: 'Navigation.CrmCompanies',
      path: '/crm/companies',
      icon: 'mat_outline:business',
    },
    {
      label: 'Navigation.CrmSegments',
      path: '/crm/intelligence/segments',
      icon: 'mat_outline:layers',
    },
    // ── BÁN HÀNG ──
    {
      label: 'Navigation.CrmLeads',
      path: '/crm/leads',
      icon: 'mat_outline:person_search',
      section: 'Navigation.Sections.CrmSelling',
    },
    {
      label: 'Navigation.CrmOpportunities',
      path: '/crm/opportunities',
      icon: 'mat_outline:account_tree',
    },
    {
      label: 'Navigation.Quotations',
      path: '/sales/quotation',
      icon: 'request_quote',
    },
    {
      label: 'Navigation.CrmForecast',
      path: '/crm/opportunities/forecast',
      icon: 'mat_outline:insights',
    },
    {
      label: 'Navigation.CrmTasks',
      path: '/crm/tasks',
      icon: 'mat_outline:event',
    },
    // ── MARKETING ──
    {
      label: 'Navigation.CrmChannels',
      path: '/crm/channels',
      icon: 'mat_outline:hub',
      section: 'Navigation.Sections.CrmMarketing',
    },
    {
      label: 'Navigation.Campaigns',
      path: '/crm/campaigns',
      icon: 'mat_outline:campaign',
    },
    {
      label: 'Navigation.CrmEmailMarketing',
      path: '/crm/email',
      icon: 'mat_outline:mail',
    },
    {
      label: 'Navigation.CrmWeb2Lead',
      path: '/crm/web2lead',
      icon: 'mat_outline:captive_portal',
    },
    // ── TỰ ĐỘNG HOÁ ──
    {
      label: 'Navigation.CrmWorkflow',
      path: '/crm/workflow',
      icon: 'mat_outline:account_tree',
      section: 'Navigation.Sections.CrmAutomation',
    },
    {
      label: 'Navigation.CrmAssignRules',
      path: '/crm/assign-rules',
      icon: 'mat_outline:rule',
    },
    {
      label: 'Navigation.CrmApprovals',
      path: '/crm/approvals',
      icon: 'mat_outline:task_alt',
    },
    // ── CHĂM SÓC ──
    {
      label: 'Navigation.CrmTickets',
      path: '/crm/service-cases',
      icon: 'mat_outline:support_agent',
      section: 'Navigation.Sections.CrmCare',
    },
    {
      label: 'Navigation.CrmKnowledge',
      path: '/crm/knowledge',
      icon: 'mat_outline:menu_book',
    },
    {
      label: 'Navigation.CrmLoyalty',
      path: '/crm/loyalty',
      icon: 'mat_outline:loyalty',
    },
    // ── PHÂN TÍCH ──
    {
      label: 'Navigation.CrmReports',
      path: '/crm/intelligence',
      icon: 'mat_outline:analytics',
      section: 'Navigation.Sections.CrmAnalytics',
    },
    {
      label: 'Navigation.CrmGoals',
      path: '/crm/goals',
      icon: 'mat_outline:flag',
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.CrmFeatureMap',
      path: '/crm/feature-map',
      icon: 'mat_outline:schema',
      section: 'Navigation.Sections.CrmHelp',
    },
    {
      label: 'Navigation.CrmGuide',
      path: '/crm/guide',
      icon: 'mat_outline:help_outline',
    },
  ],
  '/procurement': [
    {
      label: 'Navigation.PurchaseOrders',
      path: '/procurement/sourcing/purchase-order',
      icon: 'mat_outline:shopping_bag',
    },
    // Hai màn của nhóm "Đơn mua hàng" trong thiết kế Carot ERP · Procurement. Chúng có route
    // riêng nên BẮT BUỘC có mục điều hướng — `route-nav-reachability.contract.spec.ts` canh
    // đúng chiều này: màn dựng xong, route khai đủ, nhưng không ai tới được trừ khi gõ URL.
    {
      label: 'Navigation.PurchaseOrderApproval',
      path: '/procurement/sourcing/purchase-order/approval',
      icon: 'mat_outline:task_alt',
    },
    {
      label: 'Navigation.PurchaseOrderFromRequest',
      path: '/procurement/sourcing/purchase-order/from-request',
      icon: 'mat_outline:assignment_turned_in',
    },
    {
      label: 'Navigation.PurchaseOrderTemplates',
      path: '/procurement/sourcing/purchase-order/templates',
      icon: 'mat_outline:event_repeat',
    },
    {
      label: 'Navigation.PurchaseRequisitions',
      path: '/procurement/sourcing/purchase-request',
      icon: 'mat_outline:request_page',
    },
    {
      label: 'Navigation.Suppliers',
      path: '/procurement/sourcing/supplier',
      icon: 'mat_outline:local_shipping',
    },
    {
      label: 'Navigation.GoodsReceipt',
      path: '/procurement/sourcing/goods-receipt',
      icon: 'mat_outline:move_to_inbox',
    },
    // US-PUR-04 — hoá đơn NCC (đối chiếu ba chiều theo dòng) và nhóm dung sai của phép đối chiếu ấy.
    {
      label: 'Navigation.SupplierInvoice',
      path: '/procurement/sourcing/supplier-invoice',
      icon: 'mat_outline:receipt_long',
    },
    {
      label: 'Navigation.ToleranceGroups',
      path: '/procurement/sourcing/tolerance-group',
      icon: 'mat_outline:rule',
    },
  ],
  '/finance': [
    // Reconciled to the Carot "Tài chính & Kế toán" design IA (fin-shell.jsx · NAV) —
    // 12 groups · 40 features in design order. Section headers sit on the first item of
    // each group. Items whose screen does not exist yet route to the shared
    // InDevelopmentComponent ("Đang phát triển") — see modules/finance/finance.routes.ts.
    // Icons are mat_outline:* only. Legacy screens not in this design IA
    // (executive-cockpit, intelligence/{consolidated-pnl,variance-drill-down,cash-position,
    // margin-erosion}, TP/IC/consolidation sub-views, localization/packs,
    // treasury/cash-forecast) keep their routes but are intentionally not surfaced here.
    //
    // 18/09/2026 — `cost-to-serve` và `profitability` RỜI khỏi danh sách ấy. Hai màn này trả
    // lời dòng 6.3/6.8/7.3 của biểu mẫu nghiệm thu (chi phí phân bổ theo khách), và trước đợt
    // này chúng chỉ vào được bằng cách gõ URL. Một màn khách hàng phải nghiệm thu mà không có
    // lối vào thì coi như không có.
    // ── SỔ CÁI ──
    {
      label: 'Navigation.FinanceDashboard',
      path: '/finance/dashboard',
      icon: 'mat_outline:account_balance',
      section: 'Navigation.Sections.FinanceLedger',
    },
    {
      label: 'Navigation.ManualJournal',
      path: '/finance/manual-journal',
      icon: 'mat_outline:edit_note',
    },
    {
      label: 'Navigation.GLAccountLedger',
      path: '/finance/gl-account-ledger',
      icon: 'mat_outline:menu_book',
    },
    // Q3-A① (31/08) · số dư treo GR/IR — hàng về chưa hoá đơn, quá 30 ngày hiện đỏ.
    {
      label: 'Navigation.GrIrAgingReport',
      path: '/finance/grir-aging',
      icon: 'mat_outline:pending_actions',
    },
    // Q2-A (31/08) · NRV theo lô/hạn dùng — app đo, 2294 ghi bên MISA.
    {
      label: 'Navigation.NrvByLotReport',
      path: '/finance/nrv-by-lot',
      icon: 'mat_outline:ac_unit',
    },
    {
      label: 'Navigation.ChartOfAccounts',
      path: '/finance/chart-of-accounts',
      icon: 'mat_outline:account_tree',
    },
    // Sổ tay định khoản — màn tồn tại từ 08/2026 với route + resource FIN-AD (script 09/10 đã
    // gieo, 17 vai trò có grant) nhưng KHÔNG có mục sidebar: một trong 27 màn kế toán mồ côi
    // đo ngày 30/08. Kế toán vì thế không có chỗ nào xem "nghiệp vụ nào ghi Nợ/Có tài khoản
    // nào" — câu Alex hỏi thẳng trong họp 31/08 (so với MISA). Nối vào đây là toàn bộ phần
    // thiếu; đừng xây màn thứ hai.
    {
      label: 'Navigation.AccountDetermination',
      path: '/finance/account-determination',
      icon: 'mat_outline:rule',
    },
    {
      label: 'Navigation.TrialBalance',
      path: '/finance/trial-balance',
      icon: 'mat_outline:equalizer',
    },
    // ── CÔNG NỢ ──
    {
      label: 'Navigation.ARReceivables',
      path: '/finance/ar-receivables',
      icon: 'mat_outline:south_west',
      section: 'Navigation.Sections.FinancePayables',
    },
    {
      label: 'Navigation.APPayables',
      path: '/finance/ap-payables',
      icon: 'mat_outline:north_east',
    },
    {
      label: 'Navigation.Settlement',
      path: '/finance/settlement',
      icon: 'mat_outline:payments',
    },
    {
      label: 'Navigation.Disputes',
      path: '/finance/disputes',
      icon: 'mat_outline:gavel',
    },
    // ── CHỨNG TỪ THU CHI ──
    {
      label: 'Navigation.FinanceReceipt',
      path: '/finance/receipts',
      icon: 'mat_outline:south_west',
      section: 'Navigation.Sections.FinanceVouchers',
      resourceId: 'FIN-RECEIPT',
    },
    {
      label: 'Navigation.FinancePaymentVoucher',
      path: '/finance/payment-vouchers',
      icon: 'mat_outline:north_east',
      resourceId: 'FIN-PAYMENT-VOUCHER',
    },
    {
      label: 'Navigation.FinanceVatInvoice',
      path: '/finance/vat-invoices',
      icon: 'mat_outline:receipt_long',
      inDev: true,
    },
    {
      label: 'Navigation.EInvoiceProviders',
      path: '/finance/localization/einvoice-providers',
      icon: 'mat_outline:cloud',
    },
    // ── TIỀN & NGÂN QUỸ ──
    {
      label: 'Navigation.FinanceCashPosition',
      path: '/finance/banking/cash-position',
      icon: 'mat_outline:savings',
      section: 'Navigation.Sections.FinanceTreasury',
    },
    {
      label: 'Navigation.CashForecast',
      path: '/finance/banking/cash-forecast',
      icon: 'mat_outline:trending_up',
    },
    {
      label: 'Navigation.FinanceBankReconciliation',
      path: '/finance/banking/reconciliation',
      icon: 'mat_outline:compare_arrows',
    },
    {
      label: 'Navigation.BankAccounts',
      path: '/finance/banking/accounts',
      icon: 'mat_outline:account_balance',
    },
    // ── KẾ HOẠCH & NGÂN SÁCH ──
    {
      label: 'Navigation.FinanceBudget',
      path: '/finance/budgeting/list',
      icon: 'mat_outline:flag',
      section: 'Navigation.Sections.FinanceBudget',
    },
    {
      label: 'Navigation.FinanceVarianceAnalysis',
      path: '/finance/budgeting/variance',
      icon: 'mat_outline:compare_arrows',
    },
    // ── TÀI SẢN ──
    {
      label: 'Navigation.FinanceFixedAssets',
      path: '/finance/fixed-assets',
      icon: 'mat_outline:apartment',
      section: 'Navigation.Sections.FinanceAssets',
      inDev: true,
    },
    // ── DOANH THU & CHI PHÍ ──
    {
      label: 'Navigation.RevenueRecognition',
      path: '/finance/revenue-recognition',
      icon: 'mat_outline:event',
      section: 'Navigation.Sections.FinanceRevCost',
    },
    {
      label: 'Navigation.InternalOrders',
      path: '/finance/internal-orders',
      icon: 'mat_outline:assignment',
    },
    {
      label: 'Navigation.FinanceCostCenters',
      path: '/finance/cost-centers',
      icon: 'mat_outline:pie_chart',
    },
    {
      label: 'Navigation.SalesForecast',
      path: '/finance/forecast',
      icon: 'mat_outline:show_chart',
    },
    // ── HỢP NHẤT & LIÊN CÔNG TY ──
    {
      label: 'Navigation.ConsolidationGroups',
      path: '/finance/consolidation',
      icon: 'mat_outline:layers',
      section: 'Navigation.Sections.FinanceConsol',
    },
    {
      label: 'Navigation.FinanceIntercompany',
      path: '/finance/intercompany',
      icon: 'mat_outline:link',
    },
    {
      label: 'Navigation.TransferPricing',
      path: '/finance/transfer-pricing',
      icon: 'mat_outline:swap_vert',
    },
    // ── PHÂN TÍCH & RỦI RO ──
    {
      label: 'Navigation.CFOCockpit',
      path: '/finance/cfo-cockpit',
      icon: 'mat_outline:speed',
      section: 'Navigation.Sections.FinanceAnalytics',
    },
    {
      label: 'Navigation.MarginDashboard',
      path: '/finance/margin-dashboard',
      icon: 'mat_outline:auto_awesome',
    },
    {
      label: 'Navigation.CostToServe',
      path: '/finance/cost-to-serve',
      icon: 'mat_outline:local_shipping',
    },
    {
      label: 'Navigation.ProfitabilityDrilldown',
      path: '/finance/profitability',
      icon: 'mat_outline:insights',
    },
    {
      label: 'Navigation.RiskHeatmap',
      path: '/finance/intelligence/risk-heatmap',
      icon: 'mat_outline:grid_on',
    },
    {
      label: 'Navigation.InvoiceAnomaly',
      path: '/finance/invoice-anomaly',
      icon: 'mat_outline:report_problem',
    },
    {
      label: 'Navigation.ABCVariance',
      path: '/finance/abc-variance',
      icon: 'mat_outline:donut_small',
    },
    // ── BÁO CÁO & THUẾ ──
    {
      label: 'Navigation.FinancialStatements',
      path: '/finance/financial-statements',
      icon: 'mat_outline:assessment',
      section: 'Navigation.Sections.FinanceReportsTax',
    },
    {
      label: 'Navigation.TaxFinalization',
      path: '/finance/tax-finalization',
      icon: 'mat_outline:request_quote',
      inDev: true,
    },
    // ── ĐÓNG KỲ & TUÂN THỦ ──
    {
      label: 'Navigation.OpeningBalance',
      path: '/finance/opening-balance',
      icon: 'mat_outline:account_balance_wallet',
      section: 'Navigation.Sections.FinanceClose',
    },
    {
      label: 'Navigation.DocumentNumberGap',
      path: '/finance/document-number-gap',
      icon: 'mat_outline:format_list_numbered',
    },
    {
      label: 'Navigation.ReportCalendar',
      path: '/finance/localization/statutory-calendar',
      icon: 'mat_outline:event_note',
    },
    {
      label: 'Navigation.Localization',
      path: '/finance/localization',
      icon: 'mat_outline:language',
    },
    {
      label: 'Navigation.PeriodClosing',
      path: '/finance/period-closing',
      icon: 'mat_outline:event_available',
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.FinanceFeatureMap',
      path: '/finance/feature-map',
      icon: 'mat_outline:schema',
      section: 'Navigation.Sections.FinanceHelp',
      inDev: true,
    },
    {
      label: 'Navigation.FinanceGuide',
      path: '/finance/guide',
      icon: 'mat_outline:help_outline',
      inDev: true,
    },
  ],
  '/manufacturing': [
    // Reconciled to the Carot Manufacturing design IA (Carot ERP - Manufacturing App.html · NAV) —
    // 6 workflow groups: Điều hành · Định mức & Quy trình · Lệnh sản xuất · Chế biến & Đóng gói ·
    // Chất lượng & Truy xuất · Trợ giúp. Section headers on the first item of each group.
    // Dropped routes (mrp/costing/capacity-planning/demand-plans/equipment/maintenance-orders/
    // maintenance-schedules/planning-cockpit) stay reachable by URL but leave the nav (no design item;
    // maintenance is folded into the "Bảo trì (PM)" dashboard). inDev items are InDevelopment stubs.
    // ── ĐIỀU HÀNH ──
    {
      label: 'Navigation.MfgDashboard',
      path: '/manufacturing/dashboard',
      icon: 'mat_outline:dashboard',
      section: 'Navigation.Sections.MfgOperations',
    },
    {
      label: 'Navigation.PMDashboard',
      path: '/manufacturing/maintenance-dashboard',
      icon: 'mat_outline:build',
    },
    {
      label: 'Navigation.OEEDashboard',
      path: '/manufacturing/oee',
      icon: 'mat_outline:speed',
    },
    // ── ĐỊNH MỨC & QUY TRÌNH ──
    {
      label: 'Navigation.MfgBom',
      path: '/manufacturing/bom',
      icon: 'mat_outline:account_tree',
      section: 'Navigation.Sections.MfgBomProcess',
    },
    {
      label: 'Navigation.MfgRoutings',
      path: '/manufacturing/routings',
      icon: 'mat_outline:route',
    },
    {
      label: 'Navigation.WorkCenters',
      path: '/manufacturing/work-centers',
      icon: 'mat_outline:precision_manufacturing',
    },
    {
      label: 'Navigation.Workstations',
      path: '/manufacturing/workstations',
      icon: 'mat_outline:handyman',
    },
    // ── LỆNH SẢN XUẤT ──
    {
      label: 'Navigation.ProductionOrders',
      path: '/manufacturing/production-orders',
      icon: 'mat_outline:assignment',
      section: 'Navigation.Sections.MfgProduction',
    },
    {
      label: 'Navigation.MfgShopFloor',
      path: '/manufacturing/shop-floor',
      icon: 'mat_outline:factory',
    },
    // Hai chân KHO của chuỗi sản xuất (UAT01, 23/08/2026). Không có mục ở đây thì route vừa
    // nối vẫn vô hình với người dùng — mà đây đúng là hai bước bắt buộc để một lệnh sản xuất
    // đi hết vòng: cấp nguyên vật liệu ra khỏi kho, rồi nhập thành phẩm trở lại kho.
    {
      label: 'Manufacturing.MaterialIssue.Title',
      path: '/manufacturing/material-issue',
      icon: 'mat_outline:upload',
    },
    {
      label: 'Manufacturing.OutputReceipt.Title',
      path: '/manufacturing/output-receipt',
      icon: 'mat_outline:download',
    },
    {
      label: 'Navigation.ProductionLog',
      path: '/manufacturing/production-log',
      icon: 'mat_outline:history',
      inDev: true,
    },
    {
      label: 'Navigation.Subcontracting',
      path: '/manufacturing/subcontracting',
      icon: 'mat_outline:handshake',
      inDev: true,
    },
    {
      label: 'Navigation.ScrapYield',
      path: '/manufacturing/scrap',
      icon: 'mat_outline:warning_amber',
    },
    // ── CHẾ BIẾN & ĐÓNG GÓI ──
    // Mục "Lệnh chế biến" (`/manufacturing/processing`) đã GỠ 04/10/2026: route ấy nay chỉ
    // redirect sang `/manufacturing/production-orders`, vốn đã có mục riêng ở trên — giữ cả hai
    // là hai mục cùng trỏ một màn.
    {
      label: 'Navigation.PackingOrders',
      path: '/manufacturing/packing',
      icon: 'mat_outline:inventory_2',
      section: 'Navigation.Sections.MfgProcessingPacking',
    },
    {
      label: 'Navigation.WipStock',
      path: '/manufacturing/wip-tracking',
      icon: 'mat_outline:inventory',
    },
    // ── CHẤT LƯỢNG & TRUY XUẤT ──
    {
      label: 'Navigation.QualityControl',
      path: '/manufacturing/quality-control',
      icon: 'mat_outline:verified',
      section: 'Navigation.Sections.MfgQuality',
      inDev: true,
    },
    {
      label: 'Navigation.MfgLotTraceability',
      path: '/manufacturing/lot-traceability',
      icon: 'mat_outline:timeline',
      inDev: true,
    },
    {
      label: 'Navigation.HACCP',
      path: '/manufacturing/haccp',
      icon: 'mat_outline:health_and_safety',
      inDev: true,
    },
    {
      label: 'Navigation.BomExplorer',
      path: '/manufacturing/bom-explorer',
      icon: 'mat_outline:account_tree',
      inDev: true,
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.MfgFeatureMap',
      path: '/manufacturing/feature-map',
      icon: 'mat_outline:map',
      section: 'Navigation.Sections.MfgHelp',
    },
    {
      label: 'Navigation.MfgUserGuide',
      path: '/manufacturing/user-guide',
      icon: 'mat_outline:menu_book',
      inDev: true,
    },
  ],
  '/logistics': [
    // Reconciled to the Carot XNK design IA (Carot ERP - XNK App.html · XNK_NAV) — 6 workflow
    // groups: Điều hành · Vận chuyển · Chứng từ · Khai thác · Tài chính · Trợ giúp.
    // Section headers on the first item of each group. Landed-cost joined the finance group on
    // 17/09/2026 — it had a route and a seeded resource, but no way in. Chi phí đất bến (port-fees) hết stub từ UX-03.
    // ── ĐIỀU HÀNH ──
    {
      label: 'Navigation.LogisticsDashboard',
      path: '/logistics/dashboard',
      icon: 'mat_outline:dashboard',
      section: 'Navigation.Sections.Operations',
      resourceId: 'LOG-DASH',
    },
    {
      label: 'Navigation.FinanceDashboard',
      path: '/logistics/finance-dashboard',
      icon: 'mat_outline:attach_money',
      resourceId: 'LOG-FIN',
    },
    // ── VẬN CHUYỂN ──
    {
      label: 'Navigation.FreightForwarding',
      path: '/logistics/freight-forwarding',
      icon: 'mat_outline:local_shipping',
      section: 'Navigation.Sections.Transport',
      resourceId: 'LOG-XNK-HUB',
    },
    {
      label: 'Navigation.LogisticsShipment',
      path: '/logistics/freight-forwarding/delivery/shipment',
      icon: 'mat_outline:send',
      resourceId: 'LM-DLV-LM',
    },
    {
      label: 'Navigation.Routes',
      path: '/logistics/routes',
      icon: 'mat_outline:route',
      resourceId: 'LOG-RTE',
    },
    {
      label: 'Navigation.ShippingRoutes',
      path: '/logistics/shipping-routes',
      icon: 'mat_outline:route',
      resourceId: 'LOG-SHR',
    },
    {
      label: 'Navigation.Carriers',
      path: '/logistics/carriers',
      icon: 'mat_outline:local_shipping',
      resourceId: 'LOG-CAR',
    },
    // Q-025 (Alex duyệt 18/10, US-LOG-22): ba màn con của hãng GIỮ làm màn nhập theo tuyến —
    // bảng giá cước, thời gian vận chuyển, hãng ưu tiên. Tới 21/10 cả ba chỉ tới được bằng gõ URL.
    // SLA, phụ phí nhiên liệu và hiệu suất hãng đã gỡ route (xem logistics.routes.ts).
    // US-LOG-22b (Alex chốt 21/10): thêm dịch vụ của hãng vào cùng nhóm.
    {
      label: 'CarrierServices.Title',
      path: '/logistics/carrier-services',
      icon: 'mat_outline:list_alt',
      resourceId: 'LOG-CAR-SVC',
    },
    {
      label: 'RateTables.Title',
      path: '/logistics/rate-tables',
      icon: 'mat_outline:table_chart',
      resourceId: 'LOG-CAR-RT',
    },
    {
      label: 'TransitTimes.Title',
      path: '/logistics/transit-times',
      icon: 'mat_outline:schedule',
      resourceId: 'LOG-CAR-TT',
    },
    {
      label: 'PreferredCarriers.Title',
      path: '/logistics/preferred-carriers',
      icon: 'mat_outline:stars',
      resourceId: 'LOG-CAR-PRF',
    },
    // Đối tác (shipper / consignee / agent) — mục "Đối tác" trong nhóm VẬN CHUYỂN của thiết kế XNK
    // (`XNK_NAV`, hifi-xnk-domains.jsx 05/09/2026): nguồn dữ liệu cho ô gợi ý trên MBL, HBL, Booking.
    {
      label: 'Navigation.LogisticsPartners',
      path: '/logistics/partners',
      icon: 'mat_outline:groups',
      resourceId: 'LOG-PTN',
    },
    // Deliberate addition beyond the XNK design nav: the DeliveryZone master feeds the
    // sales-order ShipFeeDrawer zone picker — the screen existed but was route-only.
    {
      label: 'Navigation.DeliveryZones',
      path: '/logistics/delivery-zones',
      icon: 'mat_outline:public',
      resourceId: 'LOG-DZ',
    },
    {
      label: 'Navigation.ShipmentOrchestration',
      path: '/logistics/shipment-orchestration',
      icon: 'mat_outline:hub',
      resourceId: 'LOG-SO',
    },
    // MC4-S9: Đặt chỗ đứng TRƯỚC chứng từ vì nó là mắt xích đầu — chỗ đặt xong mới sinh
    // vận đơn nháp và lô hàng.
    {
      label: 'Navigation.Bookings',
      path: '/logistics/bookings',
      icon: 'mat_outline:event_available',
      resourceId: 'LOG-BKG',
    },
    // ── CHỨNG TỪ ──
    {
      label: 'Navigation.MasterBill',
      path: '/logistics/freight-forwarding/master-bill',
      icon: 'mat_outline:layers',
      section: 'Navigation.Sections.Documents',
      resourceId: 'LM-BOL-MB',
    },
    {
      label: 'Navigation.HouseBill',
      path: '/logistics/freight-forwarding/house-bill',
      icon: 'mat_outline:receipt_long',
      resourceId: 'LM-BOL-HB',
    },
    // US-LOG-22: tám chứng từ dưới đây trước 21/10 chỉ có lối vào qua hai trang ô vuông
    // "Vận đơn" và "Giao hàng" — không mục sidebar nào trỏ tới cả hai trang ấy, nên thực tế
    // không có lối vào. Hai trang ô vuông đã rút (chuyển hướng). `resourceId` chép từ
    // `resourceUniqueCode` của chính route; cổng route-resource-coverage đối chiếu hai bên.
    {
      label: 'DeliveryOrder.Title',
      path: '/logistics/freight-forwarding/delivery-order',
      icon: 'mat_outline:assignment',
      resourceId: 'LM-BOL-DO',
    },
    {
      label: 'LogisticInvoice.Title',
      path: '/logistics/freight-forwarding/logistic-invoice',
      icon: 'mat_outline:request_page',
      resourceId: 'LM-BOL-LI',
    },
    {
      label: 'PackageList.Title',
      path: '/logistics/freight-forwarding/package-list',
      icon: 'mat_outline:list_alt',
      resourceId: 'LM-BOL-PL',
    },
    {
      label: 'CommercialOrder.Title',
      path: '/logistics/freight-forwarding/commerce-order',
      icon: 'mat_outline:description',
      resourceId: 'LM-BOL-CO',
    },
    {
      label: 'CommercialInvoice.Title',
      path: '/logistics/freight-forwarding/commercial-invoice',
      icon: 'mat_outline:article',
      resourceId: 'LM-BOL-INV',
    },
    {
      label: 'Invoice.AccountingInvoice',
      path: '/logistics/freight-forwarding/accounting-invoice',
      icon: 'mat_outline:fact_check',
      resourceId: 'LM-BOL-INV',
    },
    {
      label: 'DeliveryNote.Title',
      path: '/logistics/freight-forwarding/delivery/delivery-note',
      icon: 'mat_outline:local_shipping',
      resourceId: 'LM-DLV-DN',
    },
    {
      label: 'DeliverySlip.Title',
      path: '/logistics/freight-forwarding/delivery/delivery-slip',
      icon: 'mat_outline:edit_note',
      resourceId: 'LM-DLV-DS',
    },
    {
      label: 'Navigation.ShipmentTypes',
      path: '/logistics/shipment-type',
      icon: 'mat_outline:local_shipping',
      resourceId: 'LOG-ST',
    },
    // ── KHAI THÁC ──
    {
      label: 'Navigation.Containers',
      path: '/logistics/containers',
      icon: 'mat_outline:inventory_2',
      section: 'Navigation.Sections.PortOps',
      resourceId: 'LOG-CON',
    },
    // Pallet đứng cạnh Container: tới 04/09 màn `/logistics/pallets` chạy được nhưng KHÔNG có
    // lối vào nào từ giao diện — sidebar không có mục, và ô hub duy nhất trỏ tới nó lại trỏ vào
    // một route chưa từng tồn tại.
    {
      label: 'Navigation.Pallets',
      path: '/logistics/pallets',
      icon: 'mat_outline:pallet',
      resourceId: 'LOG-PLT',
    },
    {
      label: 'Navigation.Demurrage',
      path: '/logistics/demurrage',
      icon: 'mat_outline:timer',
      resourceId: 'LOG-DMR',
    },
    {
      label: 'Navigation.PortFees',
      path: '/logistics/port-fees',
      icon: 'mat_outline:account_balance',
      resourceId: 'LOG-PFE',
    },
    // US-LOG-16b: lối vào báo cáo 1388/154 treo theo lô (không có lối vào là mã chết).
    {
      label: 'Navigation.PortFeeSuspense',
      path: '/logistics/port-fee-suspense',
      icon: 'mat_outline:account_balance',
      resourceId: 'LOG-PFE',
    },
    // US-LOG-22: năm màn khai thác (Sprint R-C) đã có route + mã từ trước nhưng không có lối vào.
    {
      label: 'Logistics.Drivers',
      path: '/logistics/drivers',
      icon: 'mat_outline:badge',
      resourceId: 'LOG-DRV',
    },
    {
      label: 'Logistics.DockScheduling',
      path: '/logistics/dock-scheduling',
      icon: 'mat_outline:event',
      resourceId: 'LOG-DOCK',
    },
    {
      label: 'Logistics.YardManagement',
      path: '/logistics/yard',
      icon: 'mat_outline:directions_car',
      resourceId: 'LOG-YARD',
    },
    {
      label: 'Logistics.DispatchRules',
      path: '/logistics/dispatch-rules',
      icon: 'mat_outline:rule',
      resourceId: 'LOG-DSP',
    },
    {
      label: 'Logistics.Seals.Title',
      path: '/logistics/seals',
      icon: 'mat_outline:lock',
      resourceId: 'LOG-SEAL',
    },
    // ── KẾ TOÁN ──
    // Sáu màn này ĐÃ CHẠY ĐƯỢC từ trước (route + component + endpoint đủ) nhưng KHÔNG
    // CÓ ĐƯỜNG VÀO: quét toàn bộ giao diện tìm liên kết tới
    // `/logistics/freight-forwarding/accounting` chỉ ra đúng sáu dòng — chính sáu ô của
    // hub đó trỏ vào nhau. Không thanh bên, không ô trên trang chủ, không nút nào; chỉ
    // tới được bằng cách gõ URL. Đây là đường vào (đo 24/08/2026).
    //
    // Thiết kế XNK tách nhóm KẾ TOÁN riêng khỏi TÀI CHÍNH và gộp thu/chi thành MỘT mục
    // "Phiếu thu / chi & tỷ giá". Ở đây tạm giữ hai mục vì màn thu và màn chi hiện là hai
    // component riêng; chúng gộp lại khi hộp thoại phiếu hợp nhất theo thiết kế xong.
    //
    // Sáu mã tài nguyên `LM-SAC-*` đã được gieo ở DbScript 09 và cấp ở 10 — đã kiểm, nếu
    // không thì mục sẽ vô hình với mọi tài khoản không phải admin mà không báo gì.
    {
      label: 'Receipt.Title',
      path: '/logistics/freight-forwarding/accounting/receipt',
      icon: 'mat_outline:receipt_long',
      section: 'Navigation.Sections.LogisticsAccounting',
      resourceId: 'LM-SAC-RN',
    },
    {
      label: 'Payment.Title',
      path: '/logistics/freight-forwarding/accounting/payment',
      icon: 'mat_outline:payments',
      resourceId: 'LM-SAC-PM',
    },
    {
      label: 'CustomerDebt.Title',
      path: '/logistics/freight-forwarding/accounting/customer-debt',
      icon: 'mat_outline:credit_score',
      resourceId: 'LM-SAC-CR',
    },
    {
      label: 'CustomerAccount.Title',
      path: '/logistics/freight-forwarding/accounting/customer-account',
      icon: 'mat_outline:account_balance_wallet',
      resourceId: 'LM-SAC-AB',
    },
    {
      label: 'AccountantBook.CashBook',
      path: '/logistics/freight-forwarding/accounting/cash-book',
      icon: 'mat_outline:savings',
      resourceId: 'LM-SAC-CB',
    },
    {
      label: 'AccountantBook.BankBook',
      path: '/logistics/freight-forwarding/accounting/bank-book',
      icon: 'mat_outline:account_balance',
      resourceId: 'LM-SAC-BB',
    },
    {
      label: 'FinDocs.LedgerTitle',
      path: '/logistics/freight-forwarding/accounting/financial-documents',
      icon: 'mat_outline:receipt',
      resourceId: 'LM-SAC-FD',
    },
    {
      label: 'FinDocs.PostingTitle',
      path: '/logistics/freight-forwarding/accounting/gl-posting',
      icon: 'mat_outline:fact_check',
      resourceId: 'LM-SAC-GL',
    },
    // ── TÀI CHÍNH ──
    {
      label: 'Navigation.Profitability',
      path: '/logistics/profitability',
      icon: 'mat_outline:trending_up',
      section: 'Navigation.Sections.LogisticsFinance',
      resourceId: 'LOG-PFT',
    },
    {
      label: 'Navigation.FreightAccruals',
      path: '/logistics/freight-accruals',
      icon: 'mat_outline:account_balance',
      resourceId: 'LOG-FRA',
    },
    {
      label: 'Navigation.RateContracts',
      path: '/logistics/rate-contracts',
      icon: 'mat_outline:request_quote',
      resourceId: 'LOG-FRC',
    },
    {
      label: 'Navigation.ConsolidatedBilling',
      path: '/logistics/consolidated-billing',
      icon: 'mat_outline:summarize',
      resourceId: 'LOG-CBL',
    },
    {
      label: 'Navigation.LandedCost',
      path: '/logistics/landed-cost',
      icon: 'mat_outline:receipt_long',
      resourceId: 'LOG-LCA',
    },
    {
      label: 'Navigation.CustomerProfitability',
      path: '/logistics/reports/customer-profitability',
      icon: 'mat_outline:contacts',
      resourceId: 'LOG-RPT',
    },
    {
      label: 'Navigation.RouteProfitability',
      path: '/logistics/reports/route-profitability',
      icon: 'mat_outline:alt_route',
      resourceId: 'LOG-RPT',
    },
    {
      label: 'Navigation.BranchProfitability',
      path: '/logistics/reports/branch-profitability',
      icon: 'mat_outline:business',
      resourceId: 'LOG-RPT',
    },
    {
      label: 'Navigation.RiskIntelligence',
      path: '/logistics/risk-intelligence',
      icon: 'mat_outline:warning_amber',
      resourceId: 'LOG-RI',
    },
    // ── DANH MỤC ── (US-LOG-22) Sáu danh mục logistics có route + mã từ Sprint R-C nhưng không
    // có lối vào nào; chúng nuôi picker trên lô hàng, tuyến, phí.
    {
      label: 'Logistics.TransportModes',
      path: '/logistics/transport-modes',
      icon: 'mat_outline:swap_horiz',
      section: 'Navigation.Sections.LogisticsCatalog',
      resourceId: 'LOG-TM',
    },
    {
      label: 'Logistics.TransitHubs',
      path: '/logistics/transit-hubs',
      icon: 'mat_outline:compare_arrows',
      resourceId: 'LOG-TH',
    },
    {
      label: 'Logistics.DeliveryTimeWindows',
      path: '/logistics/delivery-time-windows',
      icon: 'mat_outline:tune',
      resourceId: 'LOG-DTW',
    },
    {
      label: 'Logistics.VehicleRoutingProfiles',
      path: '/logistics/vehicle-routing-profiles',
      icon: 'mat_outline:straighten',
      resourceId: 'LOG-VRP',
    },
    {
      label: 'Logistics.AccessorialChargeTypes',
      path: '/logistics/accessorial-charge-types',
      icon: 'mat_outline:sell',
      resourceId: 'LOG-ACT',
    },
    {
      label: 'Logistics.DangerousGoodsClasses',
      path: '/logistics/dangerous-goods-classes',
      icon: 'mat_outline:warning',
      resourceId: 'LOG-DGC',
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.LogisticsFeatureMap',
      path: '/logistics/feature-map',
      icon: 'mat_outline:map',
      section: 'Navigation.Sections.LogisticsHelp',
      resourceId: 'LOG-HELP',
    },
    {
      label: 'Navigation.LogisticsUserGuide',
      path: '/logistics/user-guide',
      icon: 'mat_outline:info',
      resourceId: 'LOG-HELP',
    },
  ],
  '/hrm': [
    {
      label: 'Navigation.Dashboard',
      path: '/hrm/dashboard',
      icon: 'mat_outline:dashboard',
    },
    {
      label: 'Navigation.Employees',
      path: '/hrm/employees',
      icon: 'mat_outline:badge',
    },
    {
      label: 'Navigation.LeaveManagement',
      path: '/hrm/leave-management',
      icon: 'mat_outline:event_busy',
    },
    {
      label: 'Navigation.Timesheet',
      path: '/hrm/timesheet',
      icon: 'mat_outline:schedule',
    },
    {
      label: 'Navigation.PayrollPeriods',
      path: '/hrm/payroll/periods',
      icon: 'mat_outline:calendar_month',
    },
    {
      label: 'Navigation.PayComponents',
      path: '/hrm/payroll/pay-components',
      icon: 'mat_outline:tune',
    },
    {
      label: 'Navigation.PayrollAdjustments',
      path: '/hrm/payroll/adjustments',
      icon: 'mat_outline:history',
    },
    {
      label: 'Navigation.MyPayslips',
      path: '/hrm/payroll/my-payslips',
      icon: 'mat_outline:receipt_long',
    },
    {
      label: 'Navigation.AttendanceAnomaly',
      path: '/hrm/attendance-anomaly',
      icon: 'mat_outline:report_problem',
    },
    {
      label: 'Navigation.PerformanceDashboard',
      path: '/hrm/performance',
      icon: 'mat_outline:assessment',
    },
    {
      label: 'Navigation.GhostEmployeeAudit',
      path: '/hrm/ghost-employee-audit',
      icon: 'mat_outline:person_search',
    },
  ],
  '/planning': [
    // Reconciled to the Carot Planning design IA (Carot ERP - Planning App.html · NAV) —
    // 6 groups / 18 items: Điều hành · Kế hoạch & MRP · Kế hoạch sản xuất · Thực thi sản
    // xuất · Danh mục & báo cáo · Trợ giúp. "Bản đồ tính năng" added to TRỢ GIÚP by
    // convention (every module has one) — the one intentional deviation from the design.
    // Old nav items dropped from the sidebar (mrp-simulation) and the always-hidden routes
    // (planned-orders, supplier-lead-time, mrp-exceptions, mrp-comparison) stay URL-reachable.
    // ── ĐIỀU HÀNH ──
    {
      label: 'Navigation.PlanCockpit',
      path: '/planning/planning-dashboard',
      icon: 'mat_outline:dashboard',
      section: 'Navigation.Sections.PlanOperations',
    },
    {
      label: 'Navigation.PlanCapacity',
      path: '/planning/capacity-planning',
      icon: 'mat_outline:speed',
    },
    // ── KẾ HOẠCH & MRP ──
    {
      label: 'Navigation.DemandPlanning',
      path: '/planning/demand-plan',
      icon: 'mat_outline:show_chart',
      section: 'Navigation.Sections.PlanMrp',
    },
    {
      label: 'Navigation.PlanMps',
      path: '/planning/mps',
      icon: 'mat_outline:layers',
    },
    {
      label: 'Navigation.PlanMrpRun',
      path: '/planning/mrp-run',
      icon: 'mat_outline:autorenew',
    },
    {
      label: 'Navigation.PlanMrpSuggestions',
      path: '/planning/mrp-suggestions',
      icon: 'mat_outline:description',
    },
    // ── KẾ HOẠCH SẢN XUẤT ──
    {
      label: 'Navigation.PlanProductionPlan',
      path: '/planning/production-plan',
      icon: 'mat_outline:layers',
      section: 'Navigation.Sections.PlanProduction',
    },
    {
      label: 'Navigation.PlanProductionRequest',
      path: '/planning/production-request',
      icon: 'mat_outline:description',
    },
    {
      label: 'Navigation.PlanStationPlan',
      path: '/planning/station-plan',
      icon: 'mat_outline:monitoring',
    },
    {
      label: 'Navigation.PlanDemandVariance',
      path: '/planning/demand-variance',
      icon: 'mat_outline:show_chart',
    },
    {
      label: 'Navigation.PlanWcCapacity',
      path: '/planning/wc-capacity-schedule',
      icon: 'mat_outline:grid_view',
    },
    // Đợt 6 (27/09/2026) — KHÔNG `inDev`: màn thật, đọc `production-schedule/*` đã có ở backend.
    {
      label: 'Navigation.PlanProductionSchedules',
      path: '/planning/production-schedules',
      icon: 'mat_outline:calendar_today',
      resourceId: 'SCM-PSC',
    },
    {
      label: 'Navigation.PlanSupplyPlans',
      path: '/planning/supply-plans',
      icon: 'mat_outline:inventory',
      resourceId: 'SCM-SPL',
    },
    {
      label: 'Navigation.PlanScenarios',
      path: '/planning/planning-scenarios',
      icon: 'mat_outline:alt_route',
      resourceId: 'SCM-SCN',
    },
    // ── THỰC THI SẢN XUẤT ──
    {
      label: 'Navigation.PlanJobCards',
      path: '/planning/job-cards',
      icon: 'mat_outline:description',
      section: 'Navigation.Sections.PlanExecution',
      inDev: true,
    },
    {
      label: 'Navigation.PlanStockEntry',
      path: '/planning/stock-entry',
      icon: 'mat_outline:inventory_2',
      inDev: true,
    },
    {
      label: 'Navigation.PlanDowntime',
      path: '/planning/downtime',
      icon: 'mat_outline:warning',
      inDev: true,
    },
    // ── DANH MỤC & BÁO CÁO ──
    {
      label: 'Navigation.PlanOperations',
      path: '/planning/operations',
      icon: 'mat_outline:settings',
      section: 'Navigation.Sections.PlanCatalogReports',
      inDev: true,
    },
    {
      label: 'Navigation.PlanReports',
      path: '/planning/reports',
      icon: 'mat_outline:show_chart',
      inDev: true,
    },
    {
      label: 'Navigation.PlanSettings',
      path: '/planning/settings',
      icon: 'mat_outline:settings',
      inDev: true,
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.PlanFeatureMap',
      path: '/planning/feature-map',
      icon: 'mat_outline:map',
      section: 'Navigation.Sections.PlanHelp',
    },
    {
      label: 'Navigation.PlanUserGuide',
      path: '/planning/user-guide',
      icon: 'mat_outline:menu_book',
      inDev: true,
    },
  ],
  '/master-data': masterDataSubNav,
  // ADMIN-CAROT-01 Wave 3 — sidebar mirrors the "Carot ERP - System Admin App"
  // design 1:1 (5 groups / 22 items). Screens that used to live here but are
  // NOT in the design nav (print-management, notification-admin, internal-audit,
  // user-activity explorer/dashboard, branding, translations, preferences,
  // org-chart, setup-wizard, data-bootstrap) keep their routes and will be
  // re-homed into Platform / User Activity / Governance in a follow-up epic.
  '/administration': [
    // ── QUẢN LÝ IN ẤN ──
    // PRINT-DOC-01: sáu màn này đã dựng xong từ lâu nhưng KHÔNG mục sidebar nào trỏ tới —
    // chúng chỉ mở được bằng cách gõ thẳng URL, và bốn trong số đó nằm trong sổ nợ của
    // `route-nav-reachability`. Chứng từ in giờ là đường nghiệp vụ thật (nút In của phiếu
    // nhập kho đi qua mẫu trong CSDL), nên người quản trị phải vào được chỗ sửa mẫu.
    {
      label: 'Print.Nav.Templates',
      path: '/administration/print-management/templates',
      icon: 'mat_outline:description',
      section: 'Navigation.Sections.PrintManagement',
    },
    {
      label: 'Print.Nav.Assignments',
      path: '/administration/print-management/assignments',
      icon: 'mat_outline:rule',
    },
    {
      label: 'Print.Nav.FeatureConfig',
      path: '/administration/print-management/feature-config',
      icon: 'mat_outline:data_object',
    },
    {
      label: 'Print.Nav.Devices',
      path: '/administration/print-management/devices',
      icon: 'mat_outline:print',
    },
    {
      label: 'Print.Nav.Jobs',
      path: '/administration/print-management/jobs',
      icon: 'mat_outline:queue',
    },
    {
      label: 'Print.Nav.Logs',
      path: '/administration/print-management/logs',
      icon: 'mat_outline:history',
    },

    // ── NGƯỜI DÙNG & QUYỀN ──
    {
      label: 'Navigation.Users',
      path: '/administration/user',
      icon: 'mat_outline:person',
      section: 'Navigation.Sections.UsersPermissions',
    },
    {
      label: 'Navigation.Roles',
      path: '/administration/role',
      icon: 'mat_outline:admin_panel_settings',
    },
    {
      label: 'Navigation.Permissions',
      path: '/administration/permission',
      icon: 'mat_outline:verified_user',
    },
    {
      label: 'Navigation.Resources',
      path: '/administration/resource',
      icon: 'mat_outline:api',
    },
    {
      label: 'Navigation.UserGroups',
      path: '/administration/groups',
      icon: 'mat_outline:group',
    },
    {
      label: 'Navigation.SoD',
      path: '/administration/sod',
      icon: 'mat_outline:call_split',
    },
    // ── GÁN QUYỀN (RBAC) ──
    {
      label: 'Navigation.UserRoleAssign',
      path: '/administration/user-role',
      icon: 'mat_outline:how_to_reg',
      section: 'Navigation.Sections.RbacAssignment',
    },
    {
      label: 'Navigation.RolePermissionAssign',
      path: '/administration/role-permission',
      icon: 'mat_outline:rule',
    },
    {
      label: 'Navigation.RoleResourceAssign',
      path: '/administration/role-resource',
      icon: 'mat_outline:lan',
    },
    {
      label: 'Navigation.PermissionMatrix',
      path: '/administration/permission-matrix',
      icon: 'mat_outline:grid_on',
    },
    {
      label: 'Navigation.TenantFeatureEntitlement',
      path: '/administration/tenant-feature',
      icon: 'mat_outline:toggle_on',
    },
    // ── BẢO MẬT ──
    {
      label: 'Navigation.PasswordPolicy',
      path: '/administration/password-policy',
      icon: 'mat_outline:password',
      section: 'Navigation.Sections.Security',
    },
    {
      label: 'Navigation.Mfa',
      path: '/administration/mfa',
      icon: 'mat_outline:phonelink_lock',
    },
    {
      label: 'Navigation.Sso',
      path: '/administration/sso',
      icon: 'mat_outline:share',
    },
    {
      label: 'Activity.Sessions',
      path: '/administration/user-activity/sessions',
      icon: 'mat_outline:schedule',
    },
    // ── CẤU HÌNH ──
    {
      label: 'Navigation.TenantManagement',
      path: '/administration/tenant',
      icon: 'mat_outline:corporate_fare',
      section: 'Navigation.Sections.Configuration',
    },
    {
      label: 'Navigation.OrgTenantBu',
      path: '/administration/org-chart',
      icon: 'mat_outline:apartment',
    },
    {
      label: 'Navigation.SystemParameters',
      path: '/administration/system-parameters',
      icon: 'mat_outline:settings',
    },
    {
      label: 'Navigation.DocumentNumbering',
      path: '/administration/document-numbering',
      icon: 'mat_outline:tag',
    },
    {
      label: 'Navigation.LocalizationConfig',
      path: '/administration/localization',
      icon: 'mat_outline:language',
    },
    // ── VẬN HÀNH ──
    {
      label: 'Navigation.BackgroundJobs',
      path: '/administration/jobs',
      icon: 'mat_outline:autorenew',
      section: 'Navigation.Sections.AdminOperations',
    },
    {
      label: 'Navigation.BackupRestore',
      path: '/administration/backup',
      icon: 'mat_outline:cloud_sync',
    },
    {
      label: 'Navigation.LicensePackages',
      path: '/administration/license',
      icon: 'mat_outline:workspace_premium',
    },
    {
      label: 'Navigation.SystemHealth',
      path: '/administration/health',
      icon: 'mat_outline:monitor_heart',
    },
  ],
  // Module Bảo mật — Carot design security-module.jsx (5 groups / 18 items).
  // Items with inDev:true mount the shared InDevelopment stub (no backend yet).
  '/security': [
    // ── TỔNG QUAN ──
    {
      label: 'Navigation.SecurityCenter',
      path: '/security/overview',
      icon: 'mat_outline:speed',
      section: 'Navigation.Sections.Overview',
    },
    {
      label: 'Navigation.CompliancePosture',
      path: '/security/posture',
      icon: 'mat_outline:verified_user',
    },
    // ── QUẢN LÝ TRUY CẬP ──
    {
      label: 'Navigation.UserAccounts',
      path: '/security/accounts',
      icon: 'mat_outline:person',
      section: 'Navigation.Sections.AccessManagement',
    },
    {
      label: 'Navigation.RolesPermissions',
      path: '/security/roles',
      icon: 'mat_outline:admin_panel_settings',
    },
    {
      label: 'Navigation.JitPrivileges',
      path: '/security/jit',
      icon: 'mat_outline:schedule',
      inDev: true,
    },
    {
      label: 'Navigation.AccessReview',
      path: '/security/access-review',
      icon: 'mat_outline:fact_check',
      inDev: true,
    },
    // ── XÁC THỰC ──
    {
      label: 'Navigation.Mfa',
      path: '/security/mfa',
      icon: 'mat_outline:phonelink_lock',
      section: 'Navigation.Sections.Authentication',
    },
    {
      label: 'Navigation.SsoIdentityProviders',
      path: '/security/sso',
      icon: 'mat_outline:language',
    },
    {
      label: 'Navigation.PasswordPolicy',
      path: '/security/password-policy',
      icon: 'mat_outline:password',
    },
    {
      label: 'Navigation.ApiKeysTokens',
      path: '/security/tokens',
      icon: 'mat_outline:key',
    },
    // ── PHÁT HIỆN & PHẢN ỨNG ──
    {
      label: 'Navigation.SecurityEvents',
      path: '/security/events',
      icon: 'mat_outline:description',
      section: 'Navigation.Sections.DetectionResponse',
    },
    {
      label: 'Navigation.IntrusionDetection',
      path: '/security/threats',
      icon: 'mat_outline:radar',
      inDev: true,
    },
    {
      label: 'Navigation.VulnerabilitiesPatches',
      path: '/security/vulnerabilities',
      icon: 'mat_outline:bug_report',
      inDev: true,
    },
    {
      label: 'Navigation.SecurityIncidents',
      path: '/security/incidents',
      icon: 'mat_outline:report_problem',
      inDev: true,
    },
    // ── DỮ LIỆU & HẠ TẦNG ──
    {
      label: 'Navigation.EncryptionKms',
      path: '/security/kms',
      icon: 'mat_outline:vpn_key',
      section: 'Navigation.Sections.DataInfrastructure',
      inDev: true,
    },
    {
      label: 'Navigation.DataLossPrevention',
      path: '/security/dlp',
      icon: 'mat_outline:visibility_off',
      inDev: true,
    },
    {
      label: 'Navigation.SessionsDevices',
      path: '/security/sessions',
      icon: 'mat_outline:devices',
    },
    {
      label: 'Navigation.CertificatesTrustedIps',
      path: '/security/certificates',
      icon: 'mat_outline:workspace_premium',
      inDev: true,
    },
  ],
  '/platform': [
    // Monitoring & Ops
    {
      label: 'Navigation.Monitoring',
      path: '/platform/monitoring',
      icon: 'mat_outline:monitor_heart',
      section: 'Navigation.Sections.Monitoring',
    },
    {
      label: 'Navigation.SystemErrors',
      path: '/platform/monitoring/errors',
      icon: 'mat_outline:error_outline',
    },
    {
      label: 'Navigation.Alerts',
      path: '/platform/monitoring/alerts',
      icon: 'mat_outline:notifications_active',
    },
    {
      label: 'Navigation.Incidents',
      path: '/platform/monitoring/incidents',
      icon: 'mat_outline:report_problem',
    },
    {
      label: 'Navigation.AuditLog',
      path: '/platform/monitoring/audit',
      icon: 'mat_outline:history',
    },
    {
      label: 'Navigation.CircuitBreakers',
      path: '/platform/monitoring/circuit-breakers',
      icon: 'mat_outline:electric_bolt',
    },
    {
      label: 'Navigation.SLADashboard',
      path: '/platform/monitoring/sla-dashboard',
      icon: 'mat_outline:speed',
    },
    {
      label: 'Navigation.FeatureFlags',
      path: '/platform/monitoring/feature-flags',
      icon: 'mat_outline:flag',
    },
    {
      label: 'Navigation.DRDashboard',
      path: '/platform/monitoring/dr-dashboard',
      icon: 'mat_outline:backup',
    },
    {
      label: 'Navigation.AnomalyDetection',
      path: '/platform/monitoring/anomaly-detection',
      icon: 'mat_outline:troubleshoot',
    },
    {
      label: 'Navigation.ComplianceDashboard',
      path: '/platform/monitoring/compliance-dashboard',
      icon: 'mat_outline:verified',
    },
    {
      label: 'Navigation.PerformanceBenchmarks',
      path: '/platform/monitoring/performance-benchmarks',
      icon: 'mat_outline:speed',
    },
    {
      label: 'Navigation.ApiDocumentation',
      path: '/platform/monitoring/api-documentation',
      icon: 'mat_outline:api',
    },
    // Security
    {
      label: 'Navigation.SecurityDashboard',
      path: '/platform/security/dashboard',
      icon: 'mat_outline:shield',
      section: 'Navigation.Sections.Security',
    },
    {
      label: 'Navigation.SessionManagement',
      path: '/platform/security/sessions',
      icon: 'mat_outline:devices',
    },
    {
      label: 'Navigation.SecretRotation',
      path: '/platform/security/secrets',
      icon: 'mat_outline:key',
    },
    // Quality
    {
      label: 'Navigation.PerformanceBaselines',
      path: '/platform/quality/baselines',
      icon: 'mat_outline:speed',
      section: 'Navigation.Sections.Quality',
    },
    {
      label: 'Navigation.WebhookDelivery',
      path: '/platform/quality/webhooks',
      icon: 'mat_outline:webhook',
    },
    {
      label: 'Navigation.IntegrationHealth',
      path: '/platform/quality/health',
      icon: 'mat_outline:health_and_safety',
    },
    // Scalability
    {
      label: 'Navigation.ScalabilityDashboard',
      path: '/platform/scalability/dashboard',
      icon: 'mat_outline:dns',
      section: 'Navigation.Sections.Scalability',
    },
    {
      label: 'Navigation.PerformanceDashboard',
      path: '/platform/scalability/performance',
      icon: 'mat_outline:speed',
    },
    // Integration
    {
      label: 'Navigation.IntegrationDashboard',
      path: '/platform/integration/dashboard',
      icon: 'mat_outline:hub',
      section: 'Navigation.Sections.Integration',
    },
    {
      label: 'Navigation.WebhookManagement',
      path: '/platform/integration/webhooks',
      icon: 'mat_outline:webhook',
    },
    {
      label: 'Navigation.BIExport',
      path: '/platform/integration/bi-export',
      icon: 'mat_outline:file_download',
    },
    // Testing
    {
      label: 'Navigation.RegressionDashboard',
      path: '/platform/regression/dashboard',
      icon: 'mat_outline:bug_report',
      section: 'Navigation.Sections.Testing',
    },
    {
      label: 'Navigation.CoverageReport',
      path: '/platform/regression/coverage',
      icon: 'mat_outline:assessment',
    },
    // Hardening
    {
      label: 'Navigation.LoadTests',
      path: '/platform/hardening/load-tests',
      icon: 'mat_outline:speed',
      section: 'Navigation.Sections.Hardening',
    },
    {
      label: 'Navigation.SIEMExport',
      path: '/platform/hardening/siem-export',
      icon: 'mat_outline:security',
    },
    {
      label: 'Navigation.ReleaseCertification',
      path: '/platform/hardening/release-certification',
      icon: 'mat_outline:verified',
    },
  ],
  '/governance': [
    // ── Quy trình & phê duyệt ──────────────────────────────────────────
    {
      label: 'Navigation.ApprovalInbox',
      path: '/governance/workflow/inbox',
      icon: 'mat_outline:inbox',
      section: 'Navigation.Sections.Workflows',
    },
    {
      label: 'Navigation.ApprovalMatrix',
      path: '/governance/workflow/approval-matrix',
      icon: 'mat_outline:rule',
    },
    {
      label: 'Navigation.Delegations',
      path: '/governance/workflow/delegations',
      icon: 'mat_outline:swap_calls',
    },
    {
      label: 'Navigation.ApprovalAnalytics',
      path: '/governance/workflow/analytics',
      icon: 'mat_outline:analytics',
    },
    // ── Tuân thủ (GRC) ─────────────────────────────────────────────────
    {
      label: 'Navigation.SoDConflicts',
      path: '/governance/sod/conflicts',
      icon: 'mat_outline:security',
      section: 'Navigation.Sections.Compliance',
    },
    {
      label: 'Navigation.SoDViolations',
      path: '/governance/sod/violations',
      icon: 'mat_outline:gpp_bad',
    },
    {
      label: 'Navigation.SecurityAudit',
      path: '/governance/security-audit',
      icon: 'mat_outline:shield',
    },
    {
      label: 'Navigation.AccessReview',
      path: '/governance/access-review',
      icon: 'mat_outline:manage_accounts',
    },
    // Sổ rủi ro + Thư viện kiểm soát live in /risk and /administration but
    // surface under Compliance per the Carot governance IA (GOV-CAROT-01).
    {
      label: 'Navigation.RiskRegister',
      path: '/risk/register',
      icon: 'mat_outline:report_problem',
    },
    {
      label: 'Navigation.ControlLibrary',
      path: '/administration/internal-audit/risk-controls',
      icon: 'mat_outline:verified_user',
    },
    {
      label: 'Navigation.ControlTesting',
      path: '/governance/control-test',
      icon: 'mat_outline:fact_check',
    },
    {
      label: 'Navigation.Policy',
      path: '/governance/policy',
      icon: 'mat_outline:policy',
    },
    // ── Kiểm soát nội bộ ───────────────────────────────────────────────
    {
      label: 'Navigation.PeriodRequests',
      path: '/governance/period-requests',
      icon: 'mat_outline:event',
      section: 'Navigation.Sections.InternalControl',
    },
    {
      label: 'Navigation.ConfigTimeline',
      path: '/governance/config-timeline',
      icon: 'mat_outline:timeline',
    },
    {
      label: 'Navigation.ConfigVersions',
      path: '/governance/config-version',
      icon: 'mat_outline:history',
    },
    {
      label: 'Navigation.ChangeManagement',
      path: '/governance/change-management',
      icon: 'mat_outline:published_with_changes',
    },
    {
      label: 'Navigation.Incidents',
      path: '/platform/monitoring/incidents',
      icon: 'mat_outline:report',
    },
    {
      label: 'Navigation.AuditLog',
      path: '/platform/monitoring/audit',
      icon: 'mat_outline:fact_check',
    },
    // ── Vòng đời Tenant ────────────────────────────────────────────────
    {
      label: 'Navigation.TenantReadiness',
      path: '/governance/tenant-lifecycle/readiness',
      icon: 'mat_outline:checklist',
      section: 'Navigation.Sections.TenantLifecycle',
    },
    {
      label: 'Navigation.LifecycleTimeline',
      path: '/governance/tenant-lifecycle/timeline',
      icon: 'mat_outline:timeline',
    },
    // ── Quản lý dữ liệu ────────────────────────────────────────────────
    {
      label: 'Navigation.DataImport',
      path: '/governance/data-import',
      icon: 'mat_outline:upload_file',
      section: 'Navigation.Sections.DataManagement',
    },
    {
      label: 'Navigation.UATOrchestrator',
      path: '/governance/uat-orchestrator',
      icon: 'mat_outline:science',
    },
    {
      label: 'Navigation.DataQuality',
      path: '/governance/data-quality',
      icon: 'mat_outline:rule',
    },
    // ── Vòng đời dữ liệu ───────────────────────────────────────────────
    {
      label: 'Navigation.ArchivalDashboard',
      path: '/governance/archival/dashboard',
      icon: 'mat_outline:archive',
      section: 'Navigation.Sections.DataLifecycle',
    },
    {
      label: 'Navigation.LegalHolds',
      path: '/governance/archival/legal-holds',
      icon: 'mat_outline:gavel',
    },
    {
      label: 'Navigation.DataLifecycle',
      path: '/governance/archival/data-lifecycle',
      icon: 'mat_outline:timeline',
    },
    {
      label: 'Navigation.Privacy',
      path: '/governance/privacy',
      icon: 'mat_outline:privacy_tip',
    },
    // ── Trợ giúp ───────────────────────────────────────────────────────
    {
      label: 'Navigation.GovernanceGuide',
      path: '/governance/guide',
      icon: 'mat_outline:help_outline',
      section: 'Navigation.Sections.Help',
    },
  ],
  '/asset-management': [
    {
      label: 'Navigation.Assets',
      path: '/asset-management/assets',
      icon: 'mat_outline:business_center',
    },
    {
      label: 'Navigation.AssetTransfers',
      path: '/asset-management/transfers',
      icon: 'mat_outline:swap_horiz',
    },
    {
      label: 'Navigation.DepreciationBooks',
      path: '/asset-management/depreciation-books',
      icon: 'mat_outline:menu_book',
    },
    {
      label: 'Navigation.AssetDisposals',
      path: '/asset-management/disposals',
      icon: 'mat_outline:delete_sweep',
    },
    {
      label: 'Navigation.AssetRegister',
      path: '/asset-management/register',
      icon: 'mat_outline:list_alt',
    },
  ],
  '/project-management': [
    {
      label: 'Navigation.Projects',
      path: '/project-management/project',
      icon: 'mat_outline:folder',
    },
    {
      label: 'Navigation.ProjectAccounting',
      path: '/project-management/projects-ext',
      icon: 'mat_outline:account_tree',
    },
    {
      label: 'Navigation.ProjectGroups',
      path: '/project-management/project-group',
      icon: 'mat_outline:workspaces',
    },
  ],
  '/risk': [
    {
      label: 'Navigation.RiskDashboard',
      path: '/risk/dashboard',
      icon: 'mat_outline:dashboard',
    },
    {
      label: 'Navigation.RiskAlerts',
      path: '/risk/alert',
      icon: 'mat_outline:warning',
    },
    {
      label: 'Navigation.RiskRegisterDashboard',
      path: '/risk/register-dashboard',
      icon: 'mat_outline:assessment',
    },
    {
      label: 'Navigation.RiskRegister',
      path: '/risk/register',
      icon: 'mat_outline:policy',
    },
    {
      label: 'Navigation.MitigationTracking',
      path: '/risk/mitigations',
      icon: 'mat_outline:playlist_add_check',
    },
  ],
  '/profile': [
    {
      label: 'Navigation.ProfileAccountInfo',
      path: '/profile/info',
      icon: 'mat_outline:person',
    },
    {
      label: 'Navigation.ProfileAppearance',
      path: '/profile/preferences',
      icon: 'mat_outline:palette',
    },
    {
      label: 'Navigation.ProfileSecurity',
      path: '/profile/security',
      icon: 'mat_outline:security',
    },
    {
      label: 'Navigation.ProfileNotifications',
      path: '/profile/notifications',
      icon: 'mat_outline:notifications',
    },
    {
      label: 'Navigation.ProfileSessions',
      path: '/profile/sessions',
      icon: 'mat_outline:devices',
    },
  ],
  '/knowledge': [
    {
      label: 'Navigation.KnowledgeArticles',
      path: '/knowledge',
      icon: 'mat_outline:article',
    },
    {
      label: 'Navigation.KnowledgeSearch',
      path: '/knowledge/search',
      icon: 'mat_outline:search',
    },
    {
      label: 'Navigation.KnowledgeCategories',
      path: '/knowledge/categories',
      icon: 'mat_outline:folder',
    },
    {
      label: 'Navigation.KnowledgeInstructions',
      path: '/knowledge/instructions',
      icon: 'mat_outline:help',
    },
    {
      label: 'Navigation.KnowledgeNewArticle',
      path: '/knowledge/new',
      icon: 'mat_outline:add_circle',
    },
  ],
  '/pos': [
    // Reconciled to the Carot "Quản lý cửa hàng" design IA (store-core.jsx · NAV) —
    // 8 groups · 33 features in design order (32 design items + KitchenStations kept
    // in BÁN HÀNG per 2026-07-02 decision). Section headers sit on the first item of
    // each group. Items whose screen/API does not exist yet route to the shared
    // InDevelopmentComponent ("Đang phát triển") — see modules/pos/pos.routes.ts.
    // Icons are mat_outline:* only (design's table_restaurant/soup_kitchen are not in
    // the sprite → map/checklist substitutes).
    // ── BÁN HÀNG ──
    {
      label: 'Navigation.POS',
      path: '/pos/pos',
      icon: 'mat_outline:point_of_sale',
      section: 'Navigation.Sections.StoreSales',
    },
    {
      label: 'Navigation.PosMobile',
      path: '/pos/pos-mobile',
      icon: 'mat_outline:smartphone',
    },
    {
      label: 'Navigation.PosOrders',
      path: '/pos/orders',
      icon: 'mat_outline:receipt_long',
    },
    {
      label: 'Navigation.PosOnlineOrders',
      path: '/pos/online-orders',
      icon: 'mat_outline:storefront',
    },
    {
      label: 'Navigation.PosReservations',
      path: '/pos/reservations',
      icon: 'mat_outline:event_seat',
    },
    {
      label: 'Navigation.PosTerminal',
      path: '/pos/pos-terminal',
      icon: 'mat_outline:desktop_windows',
    },
    {
      label: 'Navigation.FloorPlan',
      path: '/pos/floor-plan',
      icon: 'mat_outline:map',
    },
    {
      label: 'Navigation.KitchenDisplay',
      path: '/pos/kitchen-display',
      icon: 'mat_outline:restaurant',
    },
    {
      label: 'Navigation.KitchenStations',
      path: '/pos/kitchen-stations',
      icon: 'mat_outline:checklist',
    },
    // ── CA & TIỀN MẶT ──
    {
      label: 'Navigation.ShiftManagement',
      path: '/pos/shift-management',
      icon: 'mat_outline:schedule',
      section: 'Navigation.Sections.StoreCash',
    },
    {
      label: 'Navigation.CashReconciliation',
      path: '/pos/cash-reconciliation',
      icon: 'mat_outline:account_balance_wallet',
    },
    {
      label: 'Navigation.VarianceReport',
      path: '/pos/variance-report',
      icon: 'mat_outline:assessment',
    },
    {
      label: 'Navigation.VarianceRules',
      path: '/pos/variance-rules',
      icon: 'mat_outline:rule',
    },
    {
      label: 'Navigation.Settlement',
      path: '/pos/settlement',
      icon: 'mat_outline:summarize',
    },
    // ── KHÁCH HÀNG & ƯU ĐÃI ──
    {
      label: 'Navigation.Promotions',
      path: '/pos/promotions',
      icon: 'mat_outline:local_offer',
      section: 'Navigation.Sections.StoreCustomers',
    },
    {
      label: 'Navigation.Loyalty',
      path: '/pos/loyalty',
      icon: 'mat_outline:stars',
    },
    {
      label: 'Navigation.GiftCards',
      path: '/pos/gift-cards',
      icon: 'mat_outline:card_giftcard',
    },
    // ── KIỂM SOÁT ──
    {
      label: 'Navigation.OverrideAuthorization',
      path: '/pos/override-authorization',
      icon: 'mat_outline:admin_panel_settings',
      section: 'Navigation.Sections.StoreControl',
    },
    {
      label: 'Navigation.CashierRiskDashboard',
      path: '/pos/cashier-risk-dashboard',
      icon: 'mat_outline:security',
    },
    {
      label: 'Navigation.FinancialPosting',
      path: '/pos/financial-posting',
      icon: 'mat_outline:account_balance',
    },
    // ── HÀNG HOÁ ──
    {
      label: 'Navigation.ModifierManagement',
      path: '/pos/modifier-management',
      icon: 'mat_outline:tune',
      section: 'Navigation.Sections.StoreGoods',
    },
    {
      label: 'Navigation.WeighingTickets',
      path: '/pos/weighing-tickets',
      icon: 'mat_outline:monitor_weight',
    },
    {
      label: 'Navigation.InventoryStatus',
      path: '/pos/inventory-status',
      icon: 'mat_outline:inventory_2',
    },
    {
      label: 'Navigation.TankManagement',
      path: '/pos/tank-management',
      icon: 'mat_outline:water',
    },
    {
      label: 'Navigation.ConsumptionReport',
      path: '/pos/consumption-report',
      icon: 'mat_outline:bar_chart',
    },
    {
      label: 'Navigation.SpoilageReport',
      path: '/pos/spoilage-report',
      icon: 'mat_outline:delete_sweep',
    },
    // ── BÁO CÁO ──
    {
      label: 'Navigation.XZReport',
      path: '/pos/xz-report',
      icon: 'mat_outline:description',
      section: 'Navigation.Sections.StoreReports',
    },
    {
      label: 'Navigation.StoreSalesReport',
      path: '/pos/sales-report',
      icon: 'mat_outline:insert_chart',
    },
    {
      label: 'Navigation.EndOfDay',
      path: '/pos/end-of-day',
      icon: 'mat_outline:task_alt',
    },
    // ── HỆ THỐNG ──
    {
      label: 'Navigation.PosDevices',
      path: '/pos/devices',
      icon: 'mat_outline:print',
      section: 'Navigation.Sections.StoreSystem',
    },
    {
      label: 'Navigation.POSSettings',
      path: '/pos/settings',
      icon: 'mat_outline:settings',
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.StoreFeatureMap',
      path: '/pos/feature-map',
      icon: 'mat_outline:schema',
      section: 'Navigation.Sections.StoreHelp',
    },
    {
      label: 'Navigation.StoreGuide',
      path: '/pos/guide',
      icon: 'mat_outline:menu_book',
    },
  ],
  '/fusion': [
    // Reconciled to the Carot "FUSION · Rule Engine" design IA (fusion-core.jsx · NAV) —
    // 5 groups · 12 features in design order. Section headers sit on the first item of
    // each group. Items whose screen does not exist yet route to the shared
    // InDevelopmentComponent ("Đang phát triển") — see modules/fusion/fusion.routes.ts.
    // Icons are mat_outline:* only. The 7 legacy screens not in this design IA
    // (categories/registry/functions/constants/approvals/approval-history/pending-instances)
    // keep their routes but are intentionally not surfaced here (100% design nav).
    // ── ĐIỀU HÀNH ──
    {
      label: 'Navigation.FusionDashboard',
      path: '/fusion/dashboard',
      icon: 'mat_outline:dashboard',
      section: 'Navigation.Sections.FusionOps',
    },
    {
      label: 'Navigation.FusionExecutionLog',
      path: '/fusion/execution-log',
      icon: 'mat_outline:history',
      inDev: true,
    },
    // ── THIẾT KẾ QUY TẮC ──
    {
      label: 'Navigation.FusionRules',
      path: '/fusion/rules',
      icon: 'mat_outline:bolt',
      section: 'Navigation.Sections.FusionRuleDesign',
    },
    {
      label: 'Navigation.FusionPendingCode',
      path: '/fusion/pending-codes',
      icon: 'mat_outline:tag',
    },
    {
      label: 'Navigation.FusionRuleSets',
      path: '/fusion/rule-sets',
      icon: 'mat_outline:layers',
    },
    {
      label: 'Navigation.FusionDecisionTables',
      path: '/fusion/decision-tables',
      icon: 'mat_outline:table_chart',
      inDev: true,
    },
    // ── TÍCH HỢP ──
    {
      label: 'Navigation.FusionIntegration',
      path: '/fusion/integration',
      icon: 'mat_outline:account_tree',
      section: 'Navigation.Sections.FusionIntegration',
      inDev: true,
    },
    {
      label: 'Navigation.FusionSteps',
      path: '/fusion/steps',
      icon: 'mat_outline:format_list_numbered',
      inDev: true,
    },
    {
      label: 'Navigation.FusionModels',
      path: '/fusion/models',
      icon: 'mat_outline:schema',
      inDev: true,
    },
    // ── KÍCH HOẠT ──
    {
      label: 'Navigation.FusionEndpoints',
      path: '/fusion/endpoints',
      icon: 'mat_outline:api',
      section: 'Navigation.Sections.FusionActivation',
      inDev: true,
    },
    {
      label: 'Navigation.FusionSimulation',
      path: '/fusion/simulation',
      icon: 'mat_outline:science',
      inDev: true,
    },
    // ── TRỢ GIÚP ──
    {
      label: 'Navigation.FusionGuide',
      path: '/fusion/guide',
      icon: 'mat_outline:menu_book',
      section: 'Navigation.Sections.FusionHelp',
      inDev: true,
    },
  ],
  '/component-library': [
    {
      label: 'ComponentLibrary.Overview',
      path: '/component-library',
      icon: 'mat_outline:home',
      exact: true,
    },
    // ── Foundations (design tokens & primitives) ──
    // Coresys "Thư viện Components" design: clean grouped sidebar, NO count
    // chips on group headers or items (per design-session decision "bỏ count").
    {
      label: 'ComponentLibrary.DsColorPalette',
      path: '/component-library/components/color-palette',
      icon: 'mat_outline:color_lens',
      section: 'ComponentLibrary.FoundationsSection',
    },
    {
      label: 'ComponentLibrary.DsTypography',
      path: '/component-library/components/typography',
      icon: 'mat_outline:text_fields',
    },
    {
      label: 'ComponentLibrary.DsLayoutGrid',
      path: '/component-library/components/layout-grid',
      icon: 'mat_outline:grid_on',
    },
    {
      label: 'ComponentLibrary.DsIconography',
      path: '/component-library/components/iconography',
      icon: 'mat_outline:emoji_symbols',
    },
    {
      label: 'ComponentLibrary.FoundationDarkMode',
      path: '/component-library/components/dark-mode',
      icon: 'mat_outline:dark_mode',
      status: 'planned',
    },
    {
      label: 'ComponentLibrary.FoundationMotion',
      path: '/component-library/components/motion',
      icon: 'mat_outline:animation',
      status: 'planned',
    },
    // ── Components (atomic ds-* primitives) ──
    {
      label: 'ComponentLibrary.DsFoundations',
      path: '/component-library/components/foundations',
      icon: 'mat_outline:layers',
      section: 'ComponentLibrary.ComponentsSection',
    },
    {
      label: 'ComponentLibrary.DsDataComponents',
      path: '/component-library/components/data',
      icon: 'mat_outline:grid_view',
    },
    {
      label: 'ComponentLibrary.DsOverlays',
      path: '/component-library/components/overlays',
      icon: 'mat_outline:picture_in_picture',
    },
    {
      label: 'ComponentLibrary.DsUtilities',
      path: '/component-library/components/utilities',
      icon: 'mat_outline:construction',
    },
    // O-12 — bộ `m-*` của /mobile. Nằm trong nhóm Components (đây là component nguyên
    // thuỷ), KHÁC với mục "MobileScreens" ở nhóm Templates vốn là khung xem trước MÀN.
    {
      label: 'ComponentLibrary.MobileKit',
      path: '/component-library/components/mobile-kit',
      icon: 'mat_outline:smartphone',
    },
    {
      label: 'ComponentLibrary.DsNavigationRef',
      path: '/component-library/components/navigation',
      icon: 'mat_outline:menu_open',
    },
    // Carot ERP v2 (US-UI-33): trang bộ component v2 — có trong library-catalog.ts nhưng chưa có
    // mục sidebar, nên khi mở nó không mục nào sáng đúng.
    {
      label: 'ComponentLibrary.DsV2Kit',
      path: '/component-library/components/v2-kit',
      icon: 'mat_outline:palette',
    },
    // ── Patterns (was: Blocks) ──
    {
      label: 'ComponentLibrary.Patterns',
      path: '/component-library/blocks',
      icon: 'mat_outline:dashboard_customize',
      section: 'ComponentLibrary.PatternsSection',
    },
    // ── Features (manifest registry) — layered-on tooling, kept per ADR-045 ──
    {
      label: 'ComponentLibrary.Features',
      path: '/component-library/features',
      icon: 'mat_outline:extension',
      section: 'ComponentLibrary.FeaturesSection',
    },
    // ── Quality (reviewer tools: prototypes + drift) ──
    {
      label: 'ComponentLibrary.Prototypes',
      path: '/component-library/prototypes',
      icon: 'mat_outline:science',
      section: 'ComponentLibrary.QualitySection',
    },
    {
      label: 'ComponentLibrary.DriftDetection',
      path: '/component-library/drift',
      icon: 'mat_outline:change_history',
    },
    // ── Templates (was: Screens — incl. Mobile) ──
    {
      label: 'ComponentLibrary.Templates',
      path: '/component-library/screens',
      icon: 'mat_outline:devices',
      section: 'ComponentLibrary.TemplatesSection',
    },
    {
      label: 'ComponentLibrary.MobileScreens',
      path: '/component-library/mobile',
      icon: 'mat_outline:phone_iphone',
    },
  ],
};
